<template>
  <div class="teste-page">
    <SiteHeader/>

    <!-- HERO -->
    <section class="hero-section">
      <v-container class="pt-16 mt-10">
        <v-row align="center">
          <v-col cols="12" md="6">
            <h1 class="hero-title">
              {{ $t('home.heroTitleLine1') }}<br/>
              {{ $t('home.heroTitleLine2') }}
            </h1>
            <p class="hero-subtitle">
              {{ $t('home.heroSubtitle') }}
            </p>
            <div class="hero-typewriter" aria-hidden="true">
              <small class="hero-typewriter-label">{{ $t('home.heroTypewriterLabel') }}</small>
              <div class="hero-typewriter-link">
                <span
                  v-for="(part, i) in typewriterVisibleParts"
                  :key="i"
                  :class="{ 'hero-typewriter-highlight': part.highlight }"
                >{{ part.text }}</span><span class="hero-typewriter-cursor">|</span>
              </div>
            </div>
            <div class="d-flex flex-wrap align-center mt-6">
              <a :href="loginHref" class="link mr-4 mb-2" v-if="!isLogged">
                <v-btn color="#0277BD" rounded x-large depressed class="btn-migly btn-migly-primary text-none px-8">
                  <v-icon class="mr-2">mdi-google</v-icon>
                  {{ $t('home.heroCtaFree') }}
                </v-btn>
              </a>
              <v-btn v-else color="#0277BD" rounded x-large depressed class="btn-migly btn-migly-primary text-none px-8 mr-4 mb-2" @click="$router.push('/painel/links/shortened')">
                {{ $t('myAccount') }}
              </v-btn>
              <a href="#recursos" class="nav-link mb-2">{{ $t('home.heroCtaHowItWorks') }}</a>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="hero-visual" :class="{ 'is-paused': isHeroAnimPaused }">
              <svg class="hero-ring hero-ring-1" viewBox="0 0 500 500" aria-hidden="true"><circle cx="250" cy="250" r="230" fill="none" stroke="#101827" stroke-width="1" stroke-dasharray="2 10"/></svg>
              <svg class="hero-ring hero-ring-2" viewBox="0 0 500 500" aria-hidden="true"><circle cx="250" cy="250" r="200" fill="none" stroke="#03A9F4" stroke-width="1"/></svg>
              <div class="hero-circle" role="img" :aria-label="$t('home.heroVisualAriaLabel')">
                <div class="hero-flow-frame hero-flow-stage-shortlink" aria-hidden="true">
                  <div class="flow-shortlink-pill">
                    <v-icon size="18" color="#0277BD">mdi-link-variant</v-icon>
                    <span class="flow-shortlink-text">migly.in/abc3</span>
                  </div>
                </div>
                <div class="hero-flow-frame hero-flow-stage-whatsapp" aria-hidden="true">
                  <div class="flow-whatsapp-bubble">
                    <div class="flow-whatsapp-link">migly.in/abc3</div>
                    <div class="flow-og-card">
                      <div class="flow-og-image">
                        <div class="flow-og-shimmer"></div>
                      </div>
                      <div class="flow-og-text">
                        <div class="flow-og-title"></div>
                        <div class="flow-og-desc"></div>
                        <div class="flow-og-domain">migly.in</div>
                      </div>
                    </div>
                    <div class="flow-whatsapp-meta">
                      <span>09:41</span>
                      <v-icon size="12" color="#4FC3F7">mdi-check-all</v-icon>
                    </div>
                  </div>
                </div>
                <div class="hero-flow-frame hero-flow-stage-biolink" aria-hidden="true">
                  <div class="flow-bio-card">
                    <div class="flow-bio-avatar"></div>
                    <span class="flow-bio-row"></span>
                    <span class="flow-bio-row flow-bio-row-sm"></span>
                    <span class="flow-bio-row"></span>
                  </div>
                </div>
                <div class="hero-flow-frame hero-flow-stage-qrcode" aria-hidden="true">
                  <div class="flow-qr-card">
                    <div class="flow-qr-grid">
                      <template v-for="(row, r) in qrMatrix">
                        <span
                          v-for="(cell, c) in row.split('')"
                          :key="r + '-' + c"
                          class="flow-qr-cell"
                          :class="{ 'flow-qr-cell-on': cell === '1' }"
                        ></span>
                      </template>
                    </div>
                    <div class="flow-qr-logo">
                      <span class="flow-qr-logo-dot flow-qr-logo-dot-1"></span>
                      <span class="flow-qr-logo-dot flow-qr-logo-dot-2"></span>
                    </div>
                  </div>
                  <div class="flow-qr-customize">
                    <v-icon size="12" color="#0277BD">mdi-palette-swatch</v-icon>
                    <span>{{ $t('home.heroCustomizable') }}</span>
                  </div>
                </div>
                <div class="hero-flow-frame hero-flow-frame-final hero-flow-stage-metric" aria-hidden="true">
                  <div class="flow-metric-panel">
                    <div class="flow-metric-bars">
                      <span class="flow-metric-bar" style="--h: 40%"></span>
                      <span class="flow-metric-bar" style="--h: 70%"></span>
                      <span class="flow-metric-bar" style="--h: 100%"></span>
                      <span class="flow-metric-bar" style="--h: 55%"></span>
                    </div>
                    <div class="flow-metric-tag">
                      <v-icon size="14" color="#0277BD">mdi-map-marker</v-icon>
                      <small>{{ $t('home.heroOriginIdentified') }}</small>
                    </div>
                  </div>
                </div>
              </div>
              <button
                type="button"
                class="hero-anim-toggle"
                :aria-label="isHeroAnimPaused ? $t('home.heroAnimResume') : $t('home.heroAnimPause')"
                @click="isHeroAnimPaused = !isHeroAnimPaused"
              >
                <v-icon size="16" color="#101827">{{ isHeroAnimPaused ? 'mdi-play' : 'mdi-pause' }}</v-icon>
              </button>
              <div class="hero-badge hero-badge-top">
                <v-icon color="#0277BD" small>mdi-content-cut</v-icon>
                <small>{{ $t('home.heroShortLinkBadge') }}</small>
              </div>
              <div class="hero-badge hero-badge-bottom">
                <transition name="social-icon-fade" mode="out-in">
                  <v-icon :key="socialIconIndex" color="#0277BD" small>{{ socialIcons[socialIconIndex] }}</v-icon>
                </transition>
                <small>{{ $t('home.heroPreviewBadge') }}</small>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- BENEFÍCIOS RÁPIDOS -->
    <section class="quick-benefits-section">
      <v-container class="pt-10 pb-12">
        <v-row justify="center">
          <v-col cols="6" md="3" v-for="(benefit, i) in benefits" :key="i">
            <div class="quick-benefit reveal" :style="{ '--reveal-index': i }">
              <div class="quick-benefit-icon">
                <div class="mini-link-pill-v2" v-if="benefit.type === 'url'">
                  <v-icon size="14" color="#0277BD">mdi-link-variant</v-icon>
                  <span class="mini-url-text">{{ $t('home.mockYourUrl') }}</span>
                  <div class="mini-edit-badge">
                    <v-icon size="10" color="#FFFFFF">mdi-pencil</v-icon>
                  </div>
                </div>
                <div class="mini-chart-bars" v-else-if="benefit.type === 'metric'">
                  <span style="--h: 35%"></span>
                  <span style="--h: 65%"></span>
                  <span style="--h: 100%"></span>
                </div>
                <div class="mini-og-card" v-else-if="benefit.type === 'preview'">
                  <div class="mini-og-image"></div>
                  <div class="mini-og-line"></div>
                  <div class="mini-og-line mini-og-line-sm"></div>
                </div>
                <svg viewBox="0 0 60 40" class="mini-trend-svg" v-else-if="benefit.type === 'analytics'" aria-hidden="true">
                  <polyline points="4,32 18,22 30,26 44,10 56,6" fill="none" stroke="#0277BD" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                  <circle cx="56" cy="6" r="4" fill="#0277BD"/>
                </svg>
              </div>
              <div class="quick-benefit-text">{{ $t(benefit.labelKey) }}</div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- PROVA SOCIAL -->
    <section class="social-proof-section" v-if="testimonials.length">
      <v-container class="pt-16 pb-16">
        <h2 class="section-title text-center mx-auto proof-headline">{{ $t('home.proofHeadline') }}</h2>
        <v-row class="mt-10">
          <v-col cols="12" sm="6" md="4" v-for="(item, i) in testimonials" :key="i">
            <div class="testimonial-card reveal" :style="{ '--reveal-index': i }">
              <p class="testimonial-quote">&ldquo;{{ $t(item.quoteKey) }}&rdquo;</p>
              <div class="testimonial-author">
                <picture>
                  <source :srcset="item.image" type="image/webp"/>
                  <img :src="item.imageFallback" :alt="item.name" loading="lazy" width="44" height="44" class="testimonial-avatar"/>
                </picture>
                <div>
                  <div class="testimonial-name">{{ item.name }}</div>
                  <div class="testimonial-role">{{ $t(item.roleKey) }}</div>
                  <a :href="item.socialHref" target="_blank" rel="noopener" class="testimonial-social">{{ item.social }}</a>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- RECURSOS / 3 PILARES -->
    <section class="pillars-section" id="recursos">
      <v-container class="pt-16 pb-16">
        <h2 class="section-title text-center mx-auto">{{ $t('home.pillarsTitle') }}</h2>
        <div class="pillars-grid mt-10">
          <div
            class="pillar-card pillar-card-featured reveal"
            style="--reveal-index: 0"
          >
            <div class="pillar-illustration pillar-illustration-origin" aria-hidden="true">
              <svg viewBox="0 0 200 120" class="origin-svg">
                <line x1="18" y1="16" x2="104" y2="58" stroke="#0277BD" stroke-width="2" stroke-dasharray="3 5" opacity="0.45"/>
                <line x1="18" y1="60" x2="104" y2="58" stroke="#0277BD" stroke-width="2" stroke-dasharray="3 5" opacity="0.7"/>
                <line x1="18" y1="104" x2="104" y2="58" stroke="#0277BD" stroke-width="2" stroke-dasharray="3 5" opacity="0.45"/>
                <circle cx="18" cy="16" r="6" fill="#0277BD" opacity="0.45"/>
                <circle cx="18" cy="60" r="6" fill="#0277BD" opacity="0.75"/>
                <circle cx="18" cy="104" r="6" fill="#0277BD" opacity="0.45"/>
                <circle cx="104" cy="58" r="24" fill="none" stroke="#101827" stroke-width="2"/>
                <circle cx="104" cy="58" r="14" fill="none" stroke="#101827" stroke-width="2"/>
                <circle cx="104" cy="58" r="4" fill="#0277BD"/>
              </svg>
            </div>
            <h3 class="pillar-title">{{ $t('home.pillarOriginTitle') }}</h3>
            <p class="pillar-desc">{{ $t('home.pillarOriginDesc') }}</p>
            <button type="button" class="pillar-toggle" @click="pillarExpanded.origem = !pillarExpanded.origem">
              {{ $t('home.pillarToggle') }}
              <v-icon size="16" :class="{ 'pillar-toggle-icon-open': pillarExpanded.origem }">mdi-chevron-down</v-icon>
            </button>
            <p class="pillar-detail" v-if="pillarExpanded.origem">{{ $t('home.pillarOriginDetail') }}</p>
          </div>
          <div class="pillars-grid-side">
            <div class="pillar-card reveal" style="--reveal-index: 1">
              <div class="pillar-illustration pillar-illustration-bio" aria-hidden="true">
                <div class="mini-link-pill"><v-icon size="12" color="#0277BD">mdi-link-variant</v-icon></div>
                <div class="mini-connector"></div>
                <div class="mini-bio-card">
                  <span class="mini-bio-avatar"></span>
                  <span class="mini-bio-row"></span>
                  <span class="mini-bio-row mini-bio-row-sm"></span>
                </div>
              </div>
              <h3 class="pillar-title">{{ $t('home.pillarBioTitle') }}</h3>
              <p class="pillar-desc">{{ $t('home.pillarBioDesc') }}</p>
              <button type="button" class="pillar-toggle" @click="pillarExpanded.bio = !pillarExpanded.bio">
                {{ $t('home.pillarToggle') }}
                <v-icon size="16" :class="{ 'pillar-toggle-icon-open': pillarExpanded.bio }">mdi-chevron-down</v-icon>
              </button>
              <p class="pillar-detail" v-if="pillarExpanded.bio">{{ $t('home.pillarBioDetail') }}</p>
            </div>
            <div class="pillar-card reveal" style="--reveal-index: 2">
              <div class="pillar-illustration pillar-illustration-extension" aria-hidden="true">
                <div class="mini-browser-wrap">
                  <div class="mini-browser">
                    <div class="mini-browser-bar">
                      <span class="mini-browser-dot"></span>
                      <span class="mini-browser-dot"></span>
                      <span class="mini-browser-dot"></span>
                    </div>
                    <div class="mini-browser-address">
                      <v-icon size="12" color="#0277BD">mdi-link-variant</v-icon>
                      <span class="mini-browser-url"></span>
                    </div>
                  </div>
                  <img src="/images/extension/icon.png" alt="" loading="lazy" width="28" height="28" class="mini-browser-extension-icon"/>
                </div>
              </div>
              <h3 class="pillar-title">{{ $t('home.pillarExtensionTitle') }}</h3>
              <p class="pillar-desc">{{ $t('home.pillarExtensionDesc') }}</p>
              <div class="pillar-actions">
                <button type="button" class="pillar-toggle" @click="pillarExpanded.extensao = !pillarExpanded.extensao">
                  {{ $t('home.pillarToggle') }}
                  <v-icon size="16" :class="{ 'pillar-toggle-icon-open': pillarExpanded.extensao }">mdi-chevron-down</v-icon>
                </button>
                <a href="https://chromewebstore.google.com/detail/miglyin/mbadgimimpdakeglbfkadhpjphbghalb" target="_blank" rel="noopener" class="pillar-toggle">
                  {{ $t('home.pillarInstallExtension') }}
                  <v-icon size="16">mdi-open-in-new</v-icon>
                </a>
              </div>
              <p class="pillar-detail" v-if="pillarExpanded.extensao">{{ $t('home.pillarExtensionDetail') }}</p>
            </div>
          </div>
        </div>
        <p class="pillars-seo-links text-center mt-10" v-html="$t('home.pillarsSeoLinksHtml')"></p>
      </v-container>
    </section>

    <!-- AGREGADOR DE LINK (BIO MIGLY) -->
    <section class="bio-section" id="bio-migly">
      <v-container class="pt-16 pb-16">
        <v-row align="center">
          <v-col cols="12" md="6">
            <div class="bio-mock reveal" style="--reveal-index: 0" aria-hidden="true">
              <div class="bio-mock-frame">
                <div class="bio-mock-avatar">
                  <v-icon size="28" color="#FFFFFF">mdi-account</v-icon>
                </div>
                <div class="bio-mock-name">{{ $t('home.bioMockHandle') }}</div>
                <div class="bio-mock-bio">{{ $t('home.bioMockTagline') }}</div>
                <div class="bio-mock-link" v-for="(link, i) in bioMockLinks" :key="i">
                  <v-icon size="16" color="#0277BD">{{ link.icon }}</v-icon>
                  <span>{{ $t(link.labelKey) }}</span>
                </div>
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <span class="promo-plan-tag bio-tag reveal" style="--reveal-index: 0">
              <v-icon size="13" color="#0277BD">mdi-link-variant</v-icon>
              {{ $t('home.bioTag') }}
            </span>
            <h2 class="section-title reveal" style="--reveal-index: 1">{{ $t('home.bioTitle') }}</h2>
            <p class="section-text mt-4 reveal" style="--reveal-index: 2">{{ $t('home.bioText') }}</p>
            <ul class="bio-list mt-6">
              <li class="reveal" style="--reveal-index: 3">
                <v-icon size="20" color="#0277BD">mdi-link-variant</v-icon>
                <div>
                  <div class="bio-list-title">{{ $t('home.bioBullet1Title') }}</div>
                  <div class="bio-list-desc">{{ $t('home.bioBullet1Desc') }}</div>
                </div>
              </li>
              <li class="reveal" style="--reveal-index: 4">
                <v-icon size="20" color="#0277BD">mdi-cursor-default-click-outline</v-icon>
                <div>
                  <div class="bio-list-title">{{ $t('home.bioBullet2Title') }}</div>
                  <div class="bio-list-desc">{{ $t('home.bioBullet2Desc') }}</div>
                </div>
              </li>
              <li class="reveal" style="--reveal-index: 5">
                <v-icon size="20" color="#0277BD">mdi-palette-swatch-outline</v-icon>
                <div>
                  <div class="bio-list-title">{{ $t('home.bioBullet3Title') }}</div>
                  <div class="bio-list-desc">{{ $t('home.bioBullet3Desc') }}</div>
                </div>
              </li>
            </ul>
          </v-col>
        </v-row>
        <v-row class="mt-8">
          <v-col cols="12">
            <p class="bio-examples-label reveal" style="--reveal-index: 6">{{ $t('home.bioExamplesLabel') }}</p>
            <div class="bio-examples-gallery-wrap">
              <div class="bio-examples-fade bio-examples-fade-left"></div>
              <div class="bio-examples-fade bio-examples-fade-right"></div>
              <div class="bio-examples-gallery" aria-hidden="true">
                <div
                  class="bio-example-phone"
                  v-for="(ex, i) in bioExamplesLoop"
                  :key="ex.name + '-' + i"
                  :style="{ '--tilt': i % 2 === 0 ? '-3deg' : '3deg' }"
                >
                  <span class="bio-example-niche">{{ $t(ex.nicheKey) }}</span>
                  <div class="bio-example-bezel">
                    <div class="bio-example-notch"></div>
                    <div class="bio-example-screen" :style="{ background: ex.backgroundColor }">
                      <div v-if="ex.avatar" class="bio-example-avatar" :style="{ backgroundImage: `url(${ex.avatar})` }"></div>
                      <div v-else class="bio-example-avatar bio-example-avatar-icon" :style="{ background: ex.sectionColor }">
                        <v-icon size="20" color="#FFFFFF">{{ ex.avatarIcon }}</v-icon>
                      </div>
                      <div class="bio-example-name" :style="{ color: ex.titleColor }">{{ ex.name }}</div>
                      <div class="bio-example-tagline" :style="{ color: ex.descriptionColor }">{{ $t(ex.descriptionKey) }}</div>
                      <div class="bio-example-link" v-for="(link, li) in ex.links" :key="li">
                        <v-icon size="13" :color="ex.sectionColor">{{ link.icon }}</v-icon>
                        <span>{{ $t(link.titleKey) }}</span>
                      </div>
                    </div>
                    <div class="bio-example-home-indicator"></div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <section class="infoproduct-section" id="pagina-de-vendas">
      <v-container class="pt-16 pb-16">
        <v-row align="center">
          <v-col cols="12" md="6">
            <div class="infoproduct-mock reveal" style="--reveal-index: 0" aria-hidden="true">
              <div class="infoproduct-mock-bezel">
                <div class="infoproduct-mock-notch"></div>
                <div class="infoproduct-mock-screen">
                  <div class="infoproduct-mock-header">
                    <div class="infoproduct-mock-logo">
                      <v-icon size="12" color="#FFFFFF">mdi-lightning-bolt</v-icon>
                    </div>
                    <span>{{ $t('home.infoproductMockBrand') }}</span>
                  </div>
                  <div class="infoproduct-mock-hero">
                    <span class="infoproduct-mock-badge">{{ $t('home.infoproductMockBadge') }}</span>
                    <h3 class="infoproduct-mock-title">{{ $t('home.infoproductMockTitle') }}</h3>
                    <p class="infoproduct-mock-desc">{{ $t('home.infoproductMockDesc') }}</p>
                    <div class="infoproduct-mock-cta">
                      <v-icon size="13" color="#FFFFFF">mdi-download</v-icon>
                      {{ $t('home.infoproductMockCta') }}
                    </div>
                  </div>
                  <ul class="infoproduct-mock-topics">
                    <li v-for="(topicKey, i) in infoproductMockTopics" :key="i">
                      <v-icon size="13" color="#0277BD">mdi-check-circle</v-icon>
                      <span>{{ $t(topicKey) }}</span>
                    </li>
                  </ul>
                  <div class="infoproduct-mock-form">
                    <span class="infoproduct-mock-form-input">{{ $t('home.infoproductMockFormLabel') }}</span>
                    <span class="infoproduct-mock-form-btn">{{ $t('home.infoproductMockFormBtn') }}</span>
                  </div>
                </div>
                <div class="infoproduct-mock-home-indicator"></div>
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <span class="promo-plan-tag bio-tag reveal" style="--reveal-index: 0">
              <v-icon size="13" color="#0277BD">mdi-cart-outline</v-icon>
              {{ $t('home.infoproductTag') }}
            </span>
            <span class="section-coming-soon-tag reveal" style="--reveal-index: 0">{{ $t('comingSoon') }}</span>
            <h2 class="section-title reveal" style="--reveal-index: 1">{{ $t('home.infoproductTitle') }}</h2>
            <p class="section-text mt-4 reveal" style="--reveal-index: 2">{{ $t('home.infoproductText') }}</p>
            <ul class="infoproduct-list mt-6">
              <li class="reveal" style="--reveal-index: 3">
                <v-icon size="20" color="#0277BD">mdi-form-select</v-icon>
                <div>
                  <div class="infoproduct-list-title">{{ $t('home.infoproductBullet1Title') }}</div>
                  <div class="infoproduct-list-desc">{{ $t('home.infoproductBullet1Desc') }}</div>
                </div>
              </li>
              <li class="reveal" style="--reveal-index: 4">
                <v-icon size="20" color="#0277BD">mdi-account-arrow-right-outline</v-icon>
                <div>
                  <div class="infoproduct-list-title">{{ $t('home.infoproductBullet2Title') }}</div>
                  <div class="infoproduct-list-desc">{{ $t('home.infoproductBullet2Desc') }}</div>
                </div>
              </li>
              <li class="reveal" style="--reveal-index: 5">
                <v-icon size="20" color="#0277BD">mdi-palette-swatch-outline</v-icon>
                <div>
                  <div class="infoproduct-list-title">{{ $t('home.infoproductBullet3Title') }}</div>
                  <div class="infoproduct-list-desc">{{ $t('home.infoproductBullet3Desc') }}</div>
                </div>
              </li>
            </ul>
            <v-btn rounded color="#0277BD" dark depressed class="btn-migly btn-migly-primary text-none px-8 mt-2 reveal" style="--reveal-index: 6" @click="goToPainelOrLogin('/painel/infoproduct')">
              {{ $t('home.infoproductCta') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- OPEN GRAPH / PRÉVIA DE COMPARTILHAMENTO -->
    <section class="og-section" id="open-graph" ref="ogSection">
      <template v-if="revealed.og">
      <v-container class="pt-16 pb-16">
        <v-row align="center">
          <v-col cols="12" md="6">
            <span class="promo-plan-tag reveal" style="--reveal-index: 0">{{ $t('home.proTag') }}</span>
            <h2 class="section-title reveal" style="--reveal-index: 1">{{ $t('home.ogTitle') }}</h2>
            <p class="section-text mt-4 reveal" style="--reveal-index: 2" v-html="$t('home.ogText1')">
            </p>
            <p class="section-text mt-4 reveal" style="--reveal-index: 3">
              {{ $t('home.ogText2') }}
            </p>
            <ul class="promo-list mt-6">
              <li class="reveal" style="--reveal-index: 4">
                <v-icon size="18" color="#0277BD">mdi-image-multiple-outline</v-icon>
                <span>{{ $t('home.ogBullet1') }}</span>
              </li>
              <li class="reveal" style="--reveal-index: 5">
                <v-icon size="18" color="#0277BD">mdi-format-title</v-icon>
                <span>{{ $t('home.ogBullet2') }}</span>
              </li>
              <li class="reveal" style="--reveal-index: 6">
                <v-icon size="18" color="#0277BD">mdi-text-box-edit-outline</v-icon>
                <span>{{ $t('home.ogBullet3') }}</span>
              </li>
            </ul>
            <a href="#planos" class="nav-link mt-2 d-inline-block reveal" style="--reveal-index: 7">{{ $t('home.viewProPlans') }}</a>
          </v-col>
          <v-col cols="12" md="6">
            <div class="og-mock reveal" style="--reveal-index: 1">
              <div class="sn-carousel-wrap" :aria-label="$t('home.ogCarouselAriaLabel')">
                <carousel
                  :perPage="1"
                  :paginationEnabled="true"
                  paginationActiveColor="#0277BD"
                  paginationColor="rgba(16,24,39,0.15)"
                  :paginationPadding="6"
                  :paginationSize="7"
                  :navigationEnabled="true"
                  navigationNextLabel="&#8250;"
                  navigationPrevLabel="&#8249;"
                  :loop="true"
                  class="sn-carousel"
                >
                  <slide>
                    <span class="sn-slide-label"><v-icon size="14" color="#25D366">mdi-whatsapp</v-icon> WhatsApp</span>
                    <div class="wa-frame">
                      <div class="wa-frame-header">
                        <div class="wa-frame-avatar">
                          <v-icon size="16" color="#FFFFFF">mdi-account</v-icon>
                        </div>
                        <div class="wa-frame-header-text">
                          <span class="wa-frame-name">Ana</span>
                          <span class="wa-frame-status">{{ $t('home.statusOnline') }}</span>
                        </div>
                        <v-icon size="18" color="#25D366" class="wa-frame-icon">mdi-whatsapp</v-icon>
                      </div>
                      <div class="wa-frame-body">
                        <div class="flow-whatsapp-bubble wa-bubble">
                          <div class="flow-whatsapp-link">migly.in/promo-inverno</div>
                          <div class="flow-og-card">
                            <div class="flow-og-image">
                              <div class="wa-loading-layer">
                                <div class="flow-og-shimmer"></div>
                              </div>
                              <div class="wa-final-layer wa-final-image">
                                <v-icon size="18" color="#FFFFFF">mdi-image</v-icon>
                              </div>
                            </div>
                            <div class="flow-og-text">
                              <div class="wa-og-text-stack">
                                <div class="wa-loading-layer wa-text-layer">
                                  <div class="flow-og-title" style="width:72%"></div>
                                  <div class="flow-og-desc" style="width:92%"></div>
                                </div>
                                <div class="wa-final-layer wa-text-layer">
                                  <div class="og-title-text">{{ $t('home.mockProductTitle') }}</div>
                                  <div class="og-desc-text">{{ $t('home.mockProductDesc') }}</div>
                                </div>
                              </div>
                              <div class="flow-og-domain">migly.in</div>
                            </div>
                          </div>
                          <div class="flow-whatsapp-meta">
                            <span>09:41</span>
                            <v-icon size="12" color="#4FC3F7">mdi-check-all</v-icon>
                          </div>
                        </div>
                      </div>
                    </div>
                  </slide>

                  <slide>
                    <span class="sn-slide-label"><v-icon size="14" color="#E1306C">mdi-instagram</v-icon> Instagram</span>
                    <div class="wa-frame">
                      <div class="wa-frame-header">
                        <div class="wa-frame-avatar ig-avatar">
                          <v-icon size="16" color="#FFFFFF">mdi-account</v-icon>
                        </div>
                        <div class="wa-frame-header-text">
                          <span class="wa-frame-name">ana.doceria</span>
                          <span class="wa-frame-status">Direct</span>
                        </div>
                        <v-icon size="18" color="#E1306C" class="wa-frame-icon">mdi-instagram</v-icon>
                      </div>
                      <div class="wa-frame-body">
                        <div class="ig-bubble wa-bubble">
                          <div class="flow-whatsapp-link">migly.in/promo-inverno</div>
                          <div class="flow-og-card">
                            <div class="flow-og-image">
                              <div class="wa-loading-layer">
                                <div class="flow-og-shimmer"></div>
                              </div>
                              <div class="wa-final-layer wa-final-image">
                                <v-icon size="18" color="#FFFFFF">mdi-image</v-icon>
                              </div>
                            </div>
                            <div class="flow-og-text">
                              <div class="wa-og-text-stack">
                                <div class="wa-loading-layer wa-text-layer">
                                  <div class="flow-og-title" style="width:72%"></div>
                                  <div class="flow-og-desc" style="width:92%"></div>
                                </div>
                                <div class="wa-final-layer wa-text-layer">
                                  <div class="og-title-text">{{ $t('home.mockProductTitle') }}</div>
                                  <div class="og-desc-text">{{ $t('home.mockProductDesc') }}</div>
                                </div>
                              </div>
                              <div class="flow-og-domain">migly.in</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </slide>

                  <slide>
                    <span class="sn-slide-label"><v-icon size="14" color="#1877F2">mdi-facebook</v-icon> Facebook</span>
                    <div class="feed-frame">
                      <div class="feed-header">
                        <div class="feed-avatar" style="background:#1877F2">
                          <v-icon size="16" color="#FFFFFF">mdi-storefront</v-icon>
                        </div>
                        <div class="feed-header-text">
                          <span class="feed-name">Loja da Ana</span>
                          <span class="feed-meta">{{ $t('home.sponsored') }}</span>
                        </div>
                        <v-icon size="18" color="#1877F2">mdi-facebook</v-icon>
                      </div>
                      <p class="feed-caption">{{ $t('home.mockFacebookCaption') }}</p>
                      <div class="flow-og-card feed-og-card">
                        <div class="flow-og-image feed-og-image">
                          <div class="wa-loading-layer">
                            <div class="flow-og-shimmer"></div>
                          </div>
                          <div class="wa-final-layer wa-final-image">
                            <v-icon size="24" color="#FFFFFF">mdi-image</v-icon>
                          </div>
                        </div>
                        <div class="flow-og-text">
                          <div class="flow-og-domain">MIGLY.IN</div>
                          <div class="wa-og-text-stack">
                            <div class="wa-loading-layer wa-text-layer">
                              <div class="flow-og-title" style="width:70%"></div>
                              <div class="flow-og-desc" style="width:88%"></div>
                            </div>
                            <div class="wa-final-layer wa-text-layer">
                              <div class="og-title-text">{{ $t('home.mockProductTitle') }}</div>
                              <div class="og-desc-text">{{ $t('home.mockProductDesc') }}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="feed-actions">
                        <span><v-icon size="15" color="#5B6472">mdi-thumb-up-outline</v-icon> {{ $t('home.actionLike') }}</span>
                        <span><v-icon size="15" color="#5B6472">mdi-comment-outline</v-icon> {{ $t('home.actionComment') }}</span>
                        <span><v-icon size="15" color="#5B6472">mdi-share-outline</v-icon> {{ $t('home.actionShare') }}</span>
                      </div>
                    </div>
                  </slide>

                  <slide>
                    <span class="sn-slide-label"><v-icon size="14" color="#0A66C2">mdi-linkedin</v-icon> LinkedIn</span>
                    <div class="feed-frame">
                      <div class="feed-header">
                        <div class="feed-avatar" style="background:#0A66C2">
                          <v-icon size="16" color="#FFFFFF">mdi-domain</v-icon>
                        </div>
                        <div class="feed-header-text">
                          <span class="feed-name">Loja da Ana</span>
                          <span class="feed-meta">{{ $t('home.followersCount') }}</span>
                        </div>
                        <v-icon size="18" color="#0A66C2">mdi-linkedin</v-icon>
                      </div>
                      <p class="feed-caption">{{ $t('home.mockLinkedinCaption') }}</p>
                      <div class="flow-og-card feed-og-card">
                        <div class="flow-og-image feed-og-image">
                          <div class="wa-loading-layer">
                            <div class="flow-og-shimmer"></div>
                          </div>
                          <div class="wa-final-layer wa-final-image">
                            <v-icon size="24" color="#FFFFFF">mdi-image</v-icon>
                          </div>
                        </div>
                        <div class="flow-og-text">
                          <div class="wa-og-text-stack">
                            <div class="wa-loading-layer wa-text-layer">
                              <div class="flow-og-title" style="width:70%"></div>
                              <div class="flow-og-desc" style="width:88%"></div>
                            </div>
                            <div class="wa-final-layer wa-text-layer">
                              <div class="og-title-text">{{ $t('home.mockProductTitle') }}</div>
                              <div class="og-desc-text">{{ $t('home.mockProductDesc') }}</div>
                            </div>
                          </div>
                          <div class="flow-og-domain">migly.in</div>
                        </div>
                      </div>
                      <div class="feed-actions">
                        <span><v-icon size="15" color="#5B6472">mdi-thumb-up-outline</v-icon> {{ $t('home.actionLike') }}</span>
                        <span><v-icon size="15" color="#5B6472">mdi-comment-outline</v-icon> {{ $t('home.actionComment') }}</span>
                        <span><v-icon size="15" color="#5B6472">mdi-share-outline</v-icon> {{ $t('home.actionShare') }}</span>
                      </div>
                    </div>
                  </slide>
                </carousel>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
      </template>
    </section>

    <!-- DIVULGAÇÃO NA TELA DE CARREGAMENTO -->
    <section class="promo-section">
      <v-container class="pt-16 pb-16">
        <v-row align="center">
          <v-col cols="12" md="6" class="order-2 order-md-1">
            <div class="promo-mock reveal" style="--reveal-index: 0" aria-hidden="true">
              <div class="promo-mock-frame">
                <span class="promo-mock-custom-tag">
                  <v-icon size="11" color="#0277BD">mdi-palette-swatch-outline</v-icon>
                  {{ $t('home.mockCustomScreen') }}
                </span>
                <div class="promo-mock-brand">
                  <div class="promo-mock-brand-logo">
                    <v-icon size="18" color="#FFFFFF">mdi-lightning-bolt</v-icon>
                  </div>
                  <span class="promo-mock-brand-name">{{ $t('home.mockBrandName') }}</span>
                </div>
                <div class="promo-mock-redirect-card">
                  <div class="promo-mock-spinner"></div>
                  <p class="promo-mock-redirect-label">{{ $t('youWillBeRedirectedTo') }}</p>
                  <p class="promo-mock-redirect-url">migly.in/promo</p>
                </div>
                <span class="promo-mock-ad-tag">
                  <v-icon size="11" color="#00A89F">mdi-cash-multiple</v-icon>
                  {{ $t('home.mockAdScreen') }}
                </span>
                <div class="promo-mock-banner">
                  <div class="promo-mock-banner-shimmer"></div>
                  <div class="promo-mock-product">
                    <div class="promo-mock-product-thumb"></div>
                    <div class="promo-mock-product-info">
                      <span class="promo-mock-product-title">{{ $t('home.mockYourProduct') }}</span>
                      <span class="promo-mock-product-price">R$ 97</span>
                    </div>
                    <span class="promo-mock-product-cta">{{ $t('home.mockSeeOffer') }}</span>
                  </div>
                </div>
                <div class="promo-mock-timer">{{ $t('home.mockRedirectingIn') }}</div>
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="6" class="order-1 order-md-2">
            <span class="promo-plan-tag reveal" style="--reveal-index: 0">
              <v-icon size="13" color="#00BDB3">mdi-cash-multiple</v-icon>
              {{ $t('home.monetizeTag') }}
            </span>
            <h2 class="section-title reveal" style="--reveal-index: 1">{{ $t('home.promoTitle') }}</h2>
            <p class="section-text mt-4 reveal" style="--reveal-index: 2">{{ $t('home.promoText') }}</p>
            <ul class="promo-list mt-6">
              <li class="reveal" style="--reveal-index: 3">
                <v-icon size="18" color="#00BDB3">mdi-cash-multiple</v-icon>
                <span>{{ $t('home.promoBullet1') }}</span>
              </li>
              <li class="reveal" style="--reveal-index: 4">
                <v-icon size="18" color="#0277BD">mdi-image-outline</v-icon>
                <span>{{ $t('home.promoBullet2') }}</span>
              </li>
              <li class="reveal" style="--reveal-index: 5">
                <v-icon size="18" color="#0277BD">mdi-timer-sand</v-icon>
                <span>{{ $t('home.promoBullet3') }}</span>
              </li>
              <li class="reveal" style="--reveal-index: 6">
                <v-icon size="18" color="#0277BD">mdi-palette-swatch-outline</v-icon>
                <span>{{ $t('home.promoBullet4') }}</span>
              </li>
            </ul>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- CALCULADORA / LEAD MAGNET -->
    <section class="calculator-section" id="calculadora" ref="calculatorSection">
      <template v-if="revealed.calculator">
      <v-container class="pt-16 pb-16">
        <v-row align="center">
          <v-col cols="12" md="6">
            <h2 class="section-title-dark">{{ $t('home.calcTitle') }}</h2>
            <p class="section-text-light mb-6">{{ $t('home.calcSubtitle') }}</p>
            <label for="calc-clicks" class="calc-label">{{ $t('home.calcLabel') }}</label>
            <input
              id="calc-clicks"
              type="range"
              min="50"
              max="5000"
              step="50"
              v-model.number="monthlyClicks"
              class="calc-slider"
            />
            <div class="calc-value">{{ monthlyClicks.toLocaleString($t('home.numberLocale')) }} {{ $t('home.clicksPerMonth') }}</div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="calc-result-card">
              <div class="calc-result-label">{{ $t('home.calcResultLabel') }}</div>
              <div class="calc-result-number">{{ monthlyDataPoints.toLocaleString($t('home.numberLocale')) }}</div>
              <div class="calc-result-caption">{{ $t('home.calcResultCaption') }}</div>
              <div class="calc-data-grid mt-4">
                <div
                  v-for="(metric, i) in trackedMetrics"
                  :key="i"
                  class="calc-data-item"
                  :class="{ 'calc-data-item-wide': metric.wide }"
                >
                  <v-icon size="16" color="#00BDB3">{{ metric.icon }}</v-icon>
                  <span>{{ $t(metric.labelKey) }}</span>
                </div>
              </div>
              <a :href="loginHref" class="link d-block mt-6" v-if="!isLogged">
                <v-btn color="#0277BD" rounded x-large depressed block class="btn-migly btn-migly-primary text-none px-8">{{ $t('home.heroCtaFree') }}</v-btn>
              </a>
              <small v-if="!isLogged" class="hero-cta-microcopy d-block text-center mt-2">{{ $t('home.noCreditCard') }}</small>
            </div>
          </v-col>
        </v-row>
        <v-row class="mt-12">
          <v-col cols="12">
            <h3 class="calc-charts-heading reveal" style="--reveal-index: 0">{{ $t('home.chartsHeading') }}</h3>
            <p class="calc-charts-subheading reveal" style="--reveal-index: 1">{{ $t('home.chartsSubheading') }}</p>
          </v-col>
          <v-col cols="12" class="reveal" style="--reveal-index: 2">
            <div class="calc-chart-card" aria-hidden="true">
              <div class="calc-chart-card-title">
                <v-icon size="16" color="#00BDB3">mdi-account-group-outline</v-icon>
                <span>{{ $t('home.metricVisitors') }}</span>
              </div>
              <div class="chart-line-legend">
                <span class="chart-line-legend-item"><i class="chart-line-dot chart-line-dot-current"></i>{{ $t('home.chartCurrentPeriod') }}</span>
                <span class="chart-line-legend-item"><i class="chart-line-dot chart-line-dot-previous"></i>{{ $t('home.chartPreviousPeriod') }}</span>
              </div>
              <div class="chart-line-canvas">
                <svg viewBox="0 0 300 130" class="chart-line-svg" preserveAspectRatio="none">
                  <polyline :points="chartVisitorsPoints.previous" fill="none" stroke="#B7C2D9" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                  <polyline :points="chartVisitorsPoints.current" fill="none" stroke="#00BDB3" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span
                  v-for="(p, i) in chartVisitorsPointsList.previous"
                  :key="`prev-${i}`"
                  class="chart-line-point chart-line-point-previous"
                  :style="{ left: (p.x / 300 * 100) + '%', top: (p.y / 130 * 100) + '%' }"
                ></span>
                <span
                  v-for="(p, i) in chartVisitorsPointsList.current"
                  :key="`cur-${i}`"
                  class="chart-line-point chart-line-point-current"
                  :style="{ left: (p.x / 300 * 100) + '%', top: (p.y / 130 * 100) + '%' }"
                ></span>
              </div>
              <div class="chart-line-labels">
                <span v-for="(day, i) in chartVisitorsDays" :key="i">{{ day }}</span>
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="6" class="reveal" style="--reveal-index: 3">
            <div class="calc-chart-card" aria-hidden="true">
              <div class="calc-chart-card-title">
                <v-icon size="16" color="#00BDB3">mdi-qrcode</v-icon>
                <span>{{ $t('home.metricQr') }}</span>
              </div>
              <div class="chart-hbar-list">
                <div class="chart-hbar-row" v-for="(item, i) in chartQrItems" :key="i">
                  <span class="chart-hbar-label">{{ item.labelKey ? $t(item.labelKey) : item.label }}</span>
                  <div class="chart-hbar-track">
                    <div class="chart-hbar-fill" :style="{ width: (item.value / chartQrMax * 100) + '%' }"></div>
                  </div>
                  <span class="chart-hbar-value">{{ item.value }}</span>
                </div>
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="6" class="reveal" style="--reveal-index: 4">
            <div class="calc-chart-card" aria-hidden="true">
              <div class="calc-chart-card-title">
                <v-icon size="16" color="#00BDB3">mdi-share-variant-outline</v-icon>
                <span>{{ $t('home.metricShare') }}</span>
              </div>
              <div class="chart-hbar-list">
                <div class="chart-hbar-row" v-for="(item, i) in chartShareItems" :key="i">
                  <span class="chart-hbar-label">{{ item.label }}</span>
                  <div class="chart-hbar-track">
                    <div class="chart-hbar-fill" :style="{ width: (item.value / chartShareMax * 100) + '%' }"></div>
                  </div>
                  <span class="chart-hbar-value">{{ item.value }}</span>
                </div>
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="6" class="reveal" style="--reveal-index: 5">
            <div class="calc-chart-card" aria-hidden="true">
              <div class="calc-chart-card-title">
                <v-icon size="16" color="#00BDB3">mdi-calendar-month-outline</v-icon>
                <span>{{ $t('home.metricPeriod') }}</span>
              </div>
              <div class="chart-columns">
                <div class="chart-column-item" v-for="(value, i) in chartPeriodValues" :key="i">
                  <div class="chart-column-bar" :style="{ '--h': (value / chartPeriodMax * 100) + '%' }"></div>
                  <span class="chart-column-label">{{ chartPeriodMonths[i] }}</span>
                </div>
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="6" class="reveal" style="--reveal-index: 6">
            <div class="calc-chart-card" aria-hidden="true">
              <div class="calc-chart-card-title">
                <v-icon size="16" color="#00BDB3">mdi-tag-multiple-outline</v-icon>
                <span>{{ $t('home.metricUtm') }}</span>
              </div>
              <div class="chart-donut-row">
                <div class="chart-donut" :style="{ background: chartUtmGradient }">
                  <div class="chart-donut-hole"></div>
                </div>
                <div class="chart-donut-legend">
                  <div class="chart-donut-legend-item" v-for="(item, i) in chartUtmItems" :key="i">
                    <i class="chart-donut-dot" :style="{ background: item.color }"></i>
                    <span>{{ item.labelKey ? $t(item.labelKey) : item.label }}</span>
                    <b>{{ Math.round(item.value / chartUtmTotal * 100) }}%</b>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
      </template>
    </section>

    <!-- API PARA DESENVOLVEDORES -->
    <section class="api-section" id="api">
      <v-container class="pt-16 pb-16">
        <v-row align="center">
          <v-col cols="12" md="6" class="order-2 order-md-1">
            <div class="api-mock reveal" style="--reveal-index: 0" aria-hidden="true">
              <div class="api-mock-frame">
                <div class="api-mock-topbar">
                  <span class="mini-browser-dot"></span>
                  <span class="mini-browser-dot"></span>
                  <span class="mini-browser-dot"></span>
                  <span class="api-mock-topbar-title">curl</span>
                </div>
                <div class="api-mock-body">
                  <div class="api-mock-line"><span class="tok-comment">{{ $t('home.apiMockCommentCreate') }}</span></div>
                  <div class="api-mock-line"><span class="tok-key">curl</span> -X POST api.migly.in/v2/online-addresses \</div>
                  <div class="api-mock-line api-mock-indent">-H <span class="tok-string">"Authorization: Bearer ****"</span> \</div>
                  <div class="api-mock-line api-mock-indent">-d <span class="tok-string">'{"originalUrl":"minhaloja.com/promo"}'</span></div>
                  <div class="api-mock-divider"></div>
                  <div class="api-mock-line"><span class="tok-comment">{{ $t('home.apiMockCommentResponse') }}</span></div>
                  <div class="api-mock-line">{</div>
                  <div class="api-mock-line api-mock-indent"><span class="tok-key">"shortUrl"</span>: <span class="tok-string">"migly.in/promo-inverno"</span></div>
                  <div class="api-mock-line">}</div>
                </div>
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="6" class="order-1 order-md-2">
            <span class="promo-plan-tag reveal" style="--reveal-index: 0">{{ $t('home.proTag') }}</span>
            <h2 class="section-title reveal" style="--reveal-index: 1">{{ $t('home.apiTitle') }}</h2>
            <p class="section-text mt-4 reveal" style="--reveal-index: 2">
              {{ $t('home.apiText') }}
            </p>
            <ul class="promo-list mt-6">
              <li class="reveal" style="--reveal-index: 3">
                <v-icon size="18" color="#0277BD">mdi-code-tags</v-icon>
                <span>{{ $t('home.apiBullet1') }}</span>
              </li>
              <li class="reveal" style="--reveal-index: 4">
                <v-icon size="18" color="#0277BD">mdi-chart-box-outline</v-icon>
                <span>{{ $t('home.apiBullet2') }}</span>
              </li>
              <li class="reveal" style="--reveal-index: 5">
                <v-icon size="18" color="#0277BD">mdi-file-document-outline</v-icon>
                <span>{{ $t('home.apiBullet3') }}</span>
              </li>
            </ul>
            <a href="#planos" class="nav-link mt-2 d-inline-block reveal" style="--reveal-index: 6">{{ $t('home.viewProPlans') }}</a>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- PLANOS -->
    <section class="plans-section" id="planos">
      <v-container class="pt-16 pb-16 text-center">
        <div class="pricing-testimonial-wrap reveal" style="--reveal-index: 0">
          <div class="testimonial-card pricing-testimonial-card text-left">
            <p class="testimonial-quote">&ldquo;{{ $t('home.pricingTestimonialQuote') }}&rdquo;</p>
            <div class="testimonial-author">
              <picture>
                <source srcset="/images/person/001-88.webp" type="image/webp"/>
                <img src="/images/person/001-88.jpg" alt="Luis Eduardo" loading="lazy" width="44" height="44" class="testimonial-avatar"/>
              </picture>
              <div>
                <div class="testimonial-name">Luis Eduardo</div>
                <div class="testimonial-role">{{ $t('home.roleDeveloper') }}</div>
                <a href="https://www.instagram.com/lss.duda/" target="_blank" rel="noopener" class="testimonial-social">@lss.duda</a>
              </div>
            </div>
          </div>
        </div>
        <div class="plans-skin">
          <SubscriptionAndPlans :screenData="screenData"/>
        </div>
      </v-container>
    </section>

    <SiteFooter/>
  </div>
</template>

<script>
import SiteHeader from "./fragments/SiteHeader.vue";
import SiteFooter from "./fragments/SiteFooter.vue";
import SubscriptionAndPlans from "./fragments/SubscriptionAndPlans.vue";
import authSession from "../../service/auth-session";
import { apiV1Auth } from "../../service/resources/api-v1-auth";

export default {
  name: 'TestePage',
  components: {
    SiteHeader,
    SiteFooter,
    SubscriptionAndPlans,
  },
  mounted: function() {
    authSession.checkSession().then((authenticated) => {
      if (authenticated) {
        const route = localStorage.route
        if (route) {
          localStorage.removeItem('route')
          this.$router.replace(route)
        } else {
          this.$router.replace('/painel/links/shortened')
        }
      } else {
        this.initGoogleOneTap()
      }
    })
    const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) {
      this.typewriterWordIndex = 3
      const fallback = this.typewriterWords[3]
      this.typewriterText = fallback.parts.map(p => p.text).join('')
    } else {
      this.runTypewriter(0)
      this.socialIconTimer = setInterval(() => {
        this.socialIconIndex = (this.socialIconIndex + 1) % this.socialIcons.length
      }, 2200)
    }
    this.setupRevealObserver()
    this.setupSectionRevealObserver()
  },
  beforeDestroy: function() {
    if (this.revealObserver) this.revealObserver.disconnect()
    if (this.sectionObserver) this.sectionObserver.disconnect()
    if (this.typewriterTimer) clearTimeout(this.typewriterTimer)
    if (this.socialIconTimer) clearInterval(this.socialIconTimer)
    if (window.google && window.google.accounts && window.google.accounts.id) {
      window.google.accounts.id.cancel()
    }
  },
  data: () => ({
    screenData: {
      auth: null,
      page: { content: [] }
    },
    isHeroAnimPaused: false,
    // Padrão fixo (não é um QR code funcional) só para ilustrar "código com marca
    // no meio": 3 "olhos" de localização (cantos) + espaço vazio central reservado
    // para o logo, como um QR code personalizável de verdade permite.
    qrMatrix: [
      '111010111',
      '101010101',
      '111101111',
      '010000101',
      '101000010',
      '001000110',
      '111101011',
      '101010100',
      '111101010',
    ],
    revealed: { og: false, calculator: false },
    sectionObserver: null,
    socialIcons: ['mdi-whatsapp', 'mdi-instagram', 'mdi-linkedin', 'mdi-facebook', 'mdi-telegram'],
    socialIconIndex: 0,
    socialIconTimer: null,
    typewriterText: '',
    typewriterWordIndex: 0,
    // Cada palavra é uma lista de partes com `highlight` marcando o que é
    // customizável (o caminho depois de "migly.in/" e o subdomínio). Ao trocar
    // de palavra, só apagamos até onde o texto das duas coincide (prefixo
    // compartilhado calculado dinamicamente) — o resto é reescrito.
    typewriterWords: [
      { parts: [{ text: 'seusite.com/produto-2024', highlight: false }] },
      { parts: [{ text: 'migly.in/', highlight: false }, { text: 'promocao-relampago', highlight: true }] },
      { parts: [{ text: 'migly.in/', highlight: false }, { text: 'curso-online', highlight: true }] },
      { parts: [{ text: 'migly.in/', highlight: false }, { text: 'tenis', highlight: true }] },
      { parts: [{ text: 'migly.in/', highlight: false }, { text: 'livro', highlight: true }] },
      { parts: [{ text: 'sua-empresa', highlight: true }, { text: '.migly.in/', highlight: false }, { text: 'links', highlight: true }] },
    ],
    typewriterTimer: null,
    monthlyClicks: 500,
    trackedMetrics: [
      { icon: 'mdi-qrcode', labelKey: 'home.metricQr' },
      { icon: 'mdi-account-group-outline', labelKey: 'home.metricVisitors' },
      { icon: 'mdi-map-marker-outline', labelKey: 'home.metricRegion' },
      { icon: 'mdi-cellphone-screenshot', labelKey: 'home.metricScreen' },
      { icon: 'mdi-share-variant-outline', labelKey: 'home.metricShare' },
      { icon: 'mdi-calendar-month-outline', labelKey: 'home.metricPeriod' },
      { icon: 'mdi-tag-multiple-outline', labelKey: 'home.metricUtm', wide: true },
    ],
    pillarExpanded: {
      origem: false,
      bio: false,
      extensao: false,
    },
    revealObserver: null,
    // Depoimentos reais reaproveitados da landing page em /landing-page (seção
    // "Depoimentos"). Nenhum deles traz um número de conversão explícito, então
    // não satisfazem 100% o requisito do spec (ver tasks.md 4.1) — mantidos por
    // serem reais e verificáveis, nunca fabricados.
    testimonials: [
      {
        quoteKey: 'home.testimonial1Quote',
        name: 'Luis Eduardo',
        roleKey: 'home.roleDeveloper',
        social: '@lss.duda',
        socialHref: 'https://www.instagram.com/lss.duda/',
        image: '/images/person/001-88.webp',
        imageFallback: '/images/person/001-88.jpg',
      },
      {
        quoteKey: 'home.testimonial2Quote',
        name: 'Jessica Portela',
        roleKey: 'home.roleSalesManager',
        social: '@jessportela__',
        socialHref: 'https://www.instagram.com/jessportela__/',
        image: '/images/person/002-88.webp',
        imageFallback: '/images/person/002-88.jpg',
      },
      {
        quoteKey: 'home.testimonial3Quote',
        name: 'Danilo Sande',
        roleKey: 'home.roleTeacher',
        social: '@prof.danilosande',
        socialHref: 'https://www.instagram.com/prof.danilosande/',
        image: '/images/person/003-88.webp',
        imageFallback: '/images/person/003-88.jpg',
      },
    ],
    benefits: [
      { type: 'url', labelKey: 'home.benefitUrl' },
      { type: 'metric', labelKey: 'home.benefitMetric' },
      { type: 'preview', labelKey: 'home.benefitPreview' },
      { type: 'analytics', labelKey: 'home.benefitAnalytics' },
    ],
    // Gráficos da seção de rastreamento: dados fixos e ilustrativos, desenhados
    // à mão em CSS/SVG (mesmo padrão do resto da página) só pra mostrar como
    // cada métrica listada acima aparece no painel de verdade — não vêm de
    // nenhuma API, propositalmente (essa seção é só visual/marketing).
    chartVisitorsSeries: {
      current: [180, 220, 260, 240, 310, 260, 340],
      previous: [140, 160, 190, 175, 220, 200, 250],
    },
    chartQrItems: [
      { label: 'QR Code', value: 320 },
      { labelKey: 'home.chartDirectLink', value: 980 },
    ],
    chartShareItems: [
      { label: 'WhatsApp', value: 540 },
      { label: 'Instagram', value: 310 },
      { label: 'Facebook', value: 150 },
      { label: 'Telegram', value: 90 },
    ],
    chartPeriodValues: [220, 340, 410, 380, 520, 610],
    chartUtmItems: [
      { label: 'Instagram', value: 420, color: '#00BDB3' },
      { label: 'Google', value: 180, color: '#0277BD' },
      { label: 'Newsletter', value: 95, color: '#4FA8E0' },
      { labelKey: 'home.chartDirectLink', value: 300, color: '#7E8EA6' },
    ],
    bioMockLinks: [
      { icon: 'mdi-instagram', labelKey: 'home.bioMockInstagram' },
      { icon: 'mdi-youtube', labelKey: 'home.bioMockYoutube' },
      { icon: 'mdi-storefront-outline', labelKey: 'home.bioMockStore' },
      { icon: 'mdi-email-outline', labelKey: 'home.bioMockContact' },
    ],
    infoproductMockTopics: [
      'home.infoproductMockTopic1',
      'home.infoproductMockTopic2',
      'home.infoproductMockTopic3',
    ],
    bioExamples: [
      {
        nicheKey: 'home.bioExampleFitnessNiche',
        name: 'Stronger!',
        descriptionKey: 'home.bioExampleFitnessDesc',
        avatar: '/images/bio-examples/fitness.jpg',
        backgroundColor: '#171F2F',
        titleColor: '#FFFFFF',
        descriptionColor: '#D7FB00',
        sectionColor: '#D7FB00',
        links: [
          { icon: 'mdi-dumbbell', titleKey: 'home.bioExampleFitnessLink1' },
          { icon: 'mdi-web', titleKey: 'home.bioExampleFitnessLink2' },
        ],
      },
      {
        nicheKey: 'home.bioExampleWellnessNiche',
        name: 'Aprenda Mindfullness',
        descriptionKey: 'home.bioExampleWellnessDesc',
        avatar: '/images/bio-examples/wellness.jpg',
        backgroundColor: '#FFFFFF',
        titleColor: '#028355',
        descriptionColor: '#028355',
        sectionColor: '#028355',
        links: [
          { icon: 'mdi-grass', titleKey: 'home.bioExampleWellnessLink1' },
          { icon: 'mdi-meditation', titleKey: 'home.bioExampleWellnessLink2' },
        ],
      },
      {
        nicheKey: 'home.bioExampleTechNiche',
        name: 'Leonardo Tec',
        descriptionKey: 'home.bioExampleTechDesc',
        avatar: '/images/bio-examples/tech.jpg',
        backgroundColor: '#101010',
        titleColor: '#FFFFFF',
        descriptionColor: '#FFFFFF',
        sectionColor: '#2986FE',
        links: [
          { icon: 'mdi-check-circle-outline', titleKey: 'home.bioExampleTechLink1' },
          { icon: 'mdi-instagram', titleKey: 'home.bioExampleTechLink2' },
        ],
      },
      {
        nicheKey: 'home.bioExampleTravelNiche',
        name: 'Viajante Curioso',
        descriptionKey: 'home.bioExampleTravelDesc',
        avatar: '/images/bio-examples/travel.jpg',
        backgroundColor: '#E8F5E9',
        titleColor: '#1B5E20',
        descriptionColor: '#4CAF50',
        sectionColor: '#FF8800',
        links: [
          { icon: 'mdi-earth', titleKey: 'home.bioExampleTravelLink1' },
          { icon: 'mdi-airplane', titleKey: 'home.bioExampleTravelLink2' },
        ],
      },
      {
        nicheKey: 'home.bioExampleFoodNiche',
        name: 'Sabor de Casa',
        descriptionKey: 'home.bioExampleFoodDesc',
        avatarIcon: 'mdi-chef-hat',
        backgroundColor: '#FFF3E0',
        titleColor: '#E65100',
        descriptionColor: '#FB8C00',
        sectionColor: '#E65100',
        links: [
          { icon: 'mdi-silverware-fork-knife', titleKey: 'home.bioExampleFoodLink1' },
          { icon: 'mdi-book-open-variant', titleKey: 'home.bioExampleFoodLink2' },
        ],
      },
      {
        nicheKey: 'home.bioExampleMusicNiche',
        name: 'DJ Nyx',
        descriptionKey: 'home.bioExampleMusicDesc',
        avatarIcon: 'mdi-music-note',
        backgroundColor: '#1A0B2E',
        titleColor: '#FFFFFF',
        descriptionColor: '#C77DFF',
        sectionColor: '#C77DFF',
        links: [
          { icon: 'mdi-spotify', titleKey: 'home.bioExampleMusicLink1' },
          { icon: 'mdi-calendar-star', titleKey: 'home.bioExampleMusicLink2' },
        ],
      },
      {
        nicheKey: 'home.bioExampleBeautyNiche',
        name: 'Bela Pele',
        descriptionKey: 'home.bioExampleBeautyDesc',
        avatarIcon: 'mdi-flower-outline',
        backgroundColor: '#FCE4EC',
        titleColor: '#C2185B',
        descriptionColor: '#E91E63',
        sectionColor: '#C2185B',
        links: [
          { icon: 'mdi-spa-outline', titleKey: 'home.bioExampleBeautyLink1' },
          { icon: 'mdi-shopping-outline', titleKey: 'home.bioExampleBeautyLink2' },
        ],
      },
      {
        nicheKey: 'home.bioExampleBusinessNiche',
        name: 'Cresça Mais',
        descriptionKey: 'home.bioExampleBusinessDesc',
        avatarIcon: 'mdi-briefcase-variant-outline',
        backgroundColor: '#101828',
        titleColor: '#FFFFFF',
        descriptionColor: '#FFC94A',
        sectionColor: '#FFC94A',
        links: [
          { icon: 'mdi-calendar-check-outline', titleKey: 'home.bioExampleBusinessLink1' },
          { icon: 'mdi-book-open-page-variant-outline', titleKey: 'home.bioExampleBusinessLink2' },
        ],
      },
    ],
  }),
  computed: {
    isLogged: function() {
      return authSession.state.authenticated
    },
    bioExamplesLoop: function() {
      return this.bioExamples.concat(this.bioExamples)
    },
    monthlyDataPoints: function() {
      return this.monthlyClicks * this.trackedMetrics.length
    },
    chartLocale: function() {
      return this.$i18n.locale === 'en' ? 'en-US' : 'pt-BR'
    },
    chartVisitorsDays: function() {
      const days = []
      for (let i = 6; i >= 0; i--) {
        const d = new Date()
        d.setDate(d.getDate() - i)
        days.push(d.toLocaleDateString(this.chartLocale, { weekday: 'short' }))
      }
      return days
    },
    chartVisitorsPointsList: function() {
      const width = 300
      const height = 110
      const { current, previous } = this.chartVisitorsSeries
      const maxVal = Math.max(...current, ...previous) * 1.15
      const stepX = width / (current.length - 1)
      const toPoints = (arr) => arr.map((v, i) => ({
        x: +(i * stepX).toFixed(1),
        y: +(height - (v / maxVal) * height).toFixed(1),
      }))
      return { current: toPoints(current), previous: toPoints(previous) }
    },
    chartVisitorsPoints: function() {
      const toStr = (points) => points.map(p => `${p.x},${p.y}`).join(' ')
      return {
        current: toStr(this.chartVisitorsPointsList.current),
        previous: toStr(this.chartVisitorsPointsList.previous),
      }
    },
    chartQrMax: function() {
      return Math.max(...this.chartQrItems.map(i => i.value))
    },
    chartShareMax: function() {
      return Math.max(...this.chartShareItems.map(i => i.value))
    },
    chartPeriodMax: function() {
      return Math.max(...this.chartPeriodValues)
    },
    chartPeriodMonths: function() {
      const months = []
      for (let i = 5; i >= 0; i--) {
        const d = new Date()
        d.setMonth(d.getMonth() - i)
        months.push(d.toLocaleDateString(this.chartLocale, { month: 'short' }))
      }
      return months
    },
    chartUtmTotal: function() {
      return this.chartUtmItems.reduce((sum, item) => sum + item.value, 0)
    },
    chartUtmGradient: function() {
      let acc = 0
      const stops = this.chartUtmItems.map(item => {
        const start = (acc / this.chartUtmTotal) * 360
        acc += item.value
        const end = (acc / this.chartUtmTotal) * 360
        return `${item.color} ${start}deg ${end}deg`
      })
      return `conic-gradient(${stops.join(', ')})`
    },
    loginHref: function() {
      return `${process.env.VUE_APP_API_V1_URL}/oauth2/authorization/google`
    },
    typewriterVisibleParts: function() {
      // Always emits one entry per `entry.parts` - never fewer - so the
      // number of <span> elements this renders stays constant while typing
      // (only each span's text content changes, from '' up to its full
      // chunk). A part-count that varied with how much text was typed was a
      // structural (child-count) hydration mismatch: the build-time
      // prerender snapshot captures this mid-animation (some parts already
      // typed), which a fresh client's first render (nothing typed yet)
      // could never structurally match - see public-page-render-handoff
      // spec. Skipping empty-chunk parts is exactly what produced that
      // mismatch, so it can't come back here.
      const entry = this.typewriterWords[this.typewriterWordIndex]
      const visibleLen = this.typewriterText.length
      let consumed = 0
      const result = []
      for (const part of entry.parts) {
        const chunk = part.text.slice(0, Math.max(0, visibleLen - consumed))
        result.push({ text: chunk, highlight: part.highlight })
        consumed += part.text.length
      }
      return result
    },
  },
  methods: {
    goToPainelOrLogin: function(route) {
      if (this.isLogged) {
        this.$router.push(route)
      } else {
        localStorage.setItem('route', route)
        window.location = this.loginHref
      }
    },
    loadGoogleIdentityScript: function() {
      return new Promise((resolve, reject) => {
        if (window.google && window.google.accounts && window.google.accounts.id) {
          resolve()
          return
        }
        const existing = document.getElementById('google-identity-script')
        if (existing) {
          existing.addEventListener('load', () => resolve())
          existing.addEventListener('error', reject)
          return
        }
        const script = document.createElement('script')
        script.id = 'google-identity-script'
        script.src = 'https://accounts.google.com/gsi/client'
        script.async = true
        script.defer = true
        script.onload = () => resolve()
        script.onerror = reject
        document.head.appendChild(script)
      })
    },
    initGoogleOneTap: function() {
      const clientId = process.env.VUE_APP_GOOGLE_CLIENT_ID
      if (!clientId) return
      this.loadGoogleIdentityScript().then(() => {
        if (!window.google || this.isLogged) return
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: this.handleGoogleCredential,
          auto_select: false,
          cancel_on_tap_outside: false,
        })
        window.google.accounts.id.prompt()
      }).catch(() => {})
    },
    handleGoogleCredential: function(response) {
      if (!response || !response.credential) return
      apiV1Auth.google(response.credential).then(() => {
        return authSession.checkSession(true)
      }).then((authenticated) => {
        if (!authenticated) return
        const route = localStorage.route
        if (route) {
          localStorage.removeItem('route')
          this.$router.push(route)
        } else {
          this.$router.push('/painel/links/shortened')
        }
      }).catch(() => {})
    },
    typeChars: function(fullText, from, to, onDone) {
      this.typewriterText = fullText.slice(0, from)
      if (from < to) {
        this.typewriterTimer = setTimeout(() => this.typeChars(fullText, from + 1, to, onDone), 70)
      } else {
        onDone()
      }
    },
    eraseChars: function(fullText, from, to, onDone) {
      this.typewriterText = fullText.slice(0, from)
      if (from > to) {
        this.typewriterTimer = setTimeout(() => this.eraseChars(fullText, from - 1, to, onDone), 35)
      } else {
        onDone()
      }
    },
    typewriterFullText: function(entry) {
      return entry.parts.map(p => p.text).join('')
    },
    typewriterSharedPrefixLength: function(a, b) {
      let i = 0
      while (i < a.length && i < b.length && a[i] === b[i]) i++
      return i
    },
    runTypewriter: function(wordIndex) {
      this.typewriterWordIndex = wordIndex
      const words = this.typewriterWords
      const current = words[wordIndex]
      const nextIndex = (wordIndex + 1) % words.length
      const next = words[nextIndex]
      const fullCurrent = this.typewriterFullText(current)
      const fullNext = this.typewriterFullText(next)
      // Só apaga até onde os textos das duas palavras coincidem — assim
      // "migly.in/" fica parado na tela enquanto só o caminho muda.
      const eraseTo = this.typewriterSharedPrefixLength(fullCurrent, fullNext)
      const startFrom = this.typewriterText.length
      this.typeChars(fullCurrent, startFrom, fullCurrent.length, () => {
        this.typewriterTimer = setTimeout(() => {
          this.eraseChars(fullCurrent, fullCurrent.length, eraseTo, () => {
            this.typewriterTimer = setTimeout(() => {
              this.typewriterWordIndex = nextIndex
              this.runTypewriter(nextIndex)
            }, 300)
          })
        }, 1400)
      })
    },
    setupRevealObserver: function() {
      const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const elements = this.$el.querySelectorAll ? this.$el.querySelectorAll('.reveal') : []
      if (prefersReducedMotion || !window.IntersectionObserver) {
        elements.forEach(el => el.classList.add('is-visible'))
        return
      }
      this.revealObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            this.revealObserver.unobserve(entry.target)
          }
        })
      }, { threshold: 0.2 })
      elements.forEach(el => this.revealObserver.observe(el))
    },
    // Registers a lazily-mounted section's own `.reveal` elements with the
    // same reveal observer/fallback `setupRevealObserver()` already set up
    // for eager content, since that one-time `querySelectorAll` at mount
    // never sees elements added later - without this they'd stay stuck at
    // opacity:0 forever (see lazy-render-homepage-below-fold-sections/design.md).
    observeNewReveals: function(container) {
      if (!container || !container.querySelectorAll) return
      const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const elements = container.querySelectorAll('.reveal')
      if (prefersReducedMotion || !window.IntersectionObserver || !this.revealObserver) {
        elements.forEach(el => el.classList.add('is-visible'))
        return
      }
      elements.forEach(el => this.revealObserver.observe(el))
    },
    revealSection: function(name, el) {
      if (this.revealed[name]) return
      this.revealed[name] = true
      this.$nextTick(() => this.observeNewReveals(el))
    },
    // Defers the `og`/`calculator` sections' render cost until they're
    // about to scroll into view. `window.__forceRevealAll` (set only by
    // the build-time prerender script) bypasses this entirely so the
    // prerendered snapshot always has complete content - see design.md.
    setupSectionRevealObserver: function() {
      const targets = [
        ['og', this.$refs.ogSection],
        ['calculator', this.$refs.calculatorSection],
      ].filter(([, el]) => el)
      if (window.__forceRevealAll || !window.IntersectionObserver) {
        targets.forEach(([name, el]) => this.revealSection(name, el))
        return
      }
      this.sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return
          const match = targets.find(([, el]) => el === entry.target)
          if (match) this.revealSection(match[0], entry.target)
          this.sectionObserver.unobserve(entry.target)
        })
      }, { rootMargin: '400px 0px' })
      targets.forEach(([, el]) => this.sectionObserver.observe(el))
    },
  },
}
</script>

<style scoped>
.teste-page {
  font-family: 'Montserrat', sans-serif;
  background: #F7F5EF;
}
.nav-link {
  display: inline-block;
  position: relative;
  color: #101827;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  margin-right: 1.75rem;
  transition: color 0.2s ease;
}
.nav-link::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -2px;
  width: 100%;
  height: 2px;
  background: #0277BD;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s cubic-bezier(0.16,1,0.3,1);
}
.nav-link:hover {
  color: #0277BD;
}
.nav-link:hover::after {
  transform: scaleX(1);
}

/*
 * Botões padronizados. O Vuetify tem uma regra global `[class*="text-"]`
 * que colide com a classe utilitária `text-none` (usada para remover o
 * uppercase padrão) e sobrescreve a cor do texto para um cinza-azulado
 * quase invisível em botões com fundo escuro. Forçamos branco explicitamente
 * com ::v-deep para não depender dessa cascata frágil.
 */
.btn-migly-primary ::v-deep .v-btn__content,
.btn-migly-dark ::v-deep .v-btn__content {
  color: #FFFFFF !important;
}
.btn-migly {
  transition: background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}
.btn-migly-primary:hover {
  background-color: #01579B !important;
  border-color: #01579B !important;
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(2,119,189,0.35);
}
.btn-migly-dark:hover {
  background-color: #0277BD !important;
  border-color: #0277BD !important;
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(16,24,39,0.3);
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Scroll reveal (respects prefers-reduced-motion) */
.reveal {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.6s cubic-bezier(0.16,1,0.3,1), transform 0.6s cubic-bezier(0.16,1,0.3,1);
  transition-delay: calc(var(--reveal-index, 0) * 90ms);
}
.reveal.is-visible {
  opacity: 1;
  transform: translateY(0);
}
@media (prefers-reduced-motion: reduce) {
  .reveal {
    transition: none !important;
    opacity: 1 !important;
    transform: none !important;
  }
}

.hero-section {
  padding-top: 5rem;
  background: #F7F5EF;
  overflow: hidden;
}
.hero-title {
  font-family: 'Montserrat', sans-serif;
  font-weight: 900;
  font-size: 3.2rem;
  line-height: 1.15;
  color: #0277BD;
  letter-spacing: -0.01em;
}
.hero-subtitle {
  color: #5B6472;
  font-size: 1.15rem;
  max-width: 480px;
  margin-top: 1.5rem;
  line-height: 1.7;
}
.hero-cta-microcopy {
  color: #5B6472;
  font-size: 0.8rem;
  margin-top: 0.4rem;
  margin-left: 0.25rem;
}

.hero-typewriter {
  margin-top: 1.25rem;
}
.hero-typewriter-label {
  display: block;
  color: #5B6472;
  font-size: 0.8rem;
  margin-bottom: 0.3rem;
}
.hero-typewriter-link {
  font-family: 'Montserrat', sans-serif;
  font-weight: 800;
  font-size: 1.3rem;
  color: #0277BD;
  min-height: 1.7rem;
  word-break: break-all;
}
.hero-typewriter-highlight {
  color: #00BDB3;
  text-decoration: underline;
  text-decoration-style: dashed;
  text-decoration-color: rgba(0,189,179,0.5);
  text-underline-offset: 3px;
}
.hero-typewriter-cursor {
  display: inline-block;
  margin-left: 2px;
  animation: hero-typewriter-blink 1s step-end infinite;
}
@keyframes hero-typewriter-blink {
  50% { opacity: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .hero-typewriter-cursor {
    animation: none;
  }
}

.hero-visual {
  position: relative;
  width: 100%;
  max-width: 460px;
  aspect-ratio: 1/1;
  margin: 2rem auto 0;
}
.hero-ring {
  position: absolute;
  top: 0; left: 0;
  width: 100%;
  height: 100%;
}
.hero-circle {
  position: absolute;
  top: 8%;
  left: 8%;
  width: 84%;
  height: 84%;
  border-radius: 50%;
  background: #0277BD;
  overflow: hidden;
  box-shadow: 0 30px 60px rgba(0,121,241,0.25);
}
.hero-flow-frame {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  opacity: 0;
  animation: hero-flow-cycle 20s infinite;
}
.hero-flow-frame:nth-child(1) { animation-delay: 0s; }
.hero-flow-frame:nth-child(2) { animation-delay: 4s; }
.hero-flow-frame:nth-child(3) { animation-delay: 8s; }
.hero-flow-frame:nth-child(4) { animation-delay: 12s; }
.hero-flow-frame:nth-child(5) { animation-delay: 16s; }
.hero-visual.is-paused .hero-flow-frame {
  animation-play-state: paused;
}
.hero-visual.is-paused .flow-og-shimmer {
  animation-play-state: paused;
}
@keyframes hero-flow-cycle {
  0%, 1.2% { opacity: 0; }
  3.2%, 17.6% { opacity: 1; }
  20.8%, 100% { opacity: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .hero-flow-frame {
    animation: none !important;
    opacity: 0 !important;
  }
  .hero-flow-frame-final {
    opacity: 1 !important;
  }
}

/* Estágio 1: link curto (efeito de "pop" ao surgir) */
.flow-shortlink-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #FFFFFF;
  border-radius: 999px;
  padding: 0.9rem 1.3rem;
  box-shadow: 0 20px 40px rgba(16,24,39,0.18);
  animation: shortlink-pop 20s infinite;
  animation-delay: 0s;
}
.hero-visual.is-paused .flow-shortlink-pill {
  animation-play-state: paused;
}
@keyframes shortlink-pop {
  0%, 2% { transform: scale(0.5); }
  6% { transform: scale(1.08); }
  10%, 100% { transform: scale(1); }
}
.flow-shortlink-text {
  font-weight: 700;
  font-size: 0.95rem;
  color: #101827;
}

/* Estágio 2: compartilhado no WhatsApp, preview de Open Graph carregando */
.flow-whatsapp-bubble {
  width: 190px;
  background: #DCF8C6;
  border-radius: 1rem 1rem 1rem 0.2rem;
  padding: 0.6rem 0.6rem 0.4rem;
  box-shadow: 0 20px 40px rgba(16,24,39,0.18);
}
.flow-whatsapp-link {
  color: #0277BD;
  text-decoration: underline;
  font-size: 0.78rem;
  font-weight: 600;
  margin-bottom: 0.4rem;
}
.flow-og-card {
  background: #FFFFFF;
  border-radius: 0.6rem;
  overflow: hidden;
  border: 1px solid rgba(16,24,39,0.06);
}
.flow-og-image {
  position: relative;
  width: 100%;
  height: 64px;
  background: #E2E9EE;
  overflow: hidden;
}
.flow-og-shimmer {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.75) 50%, rgba(255,255,255,0) 100%);
  background-size: 200% 100%;
  animation: og-shimmer-sweep 1.3s linear infinite;
}
@keyframes og-shimmer-sweep {
  0% { background-position: 150% 0; }
  100% { background-position: -50% 0; }
}
@media (prefers-reduced-motion: reduce) {
  .flow-og-shimmer {
    display: none;
  }
}
.flow-og-text {
  padding: 0.5rem 0.6rem 0.6rem;
}
.flow-og-title,
.flow-og-desc {
  height: 6px;
  border-radius: 3px;
  background: rgba(16,24,39,0.16);
  margin-bottom: 5px;
}
.flow-og-title {
  width: 78%;
}
.flow-og-desc {
  width: 92%;
  opacity: 0.7;
}
.flow-og-domain {
  font-size: 0.62rem;
  color: #8892A0;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-top: 0.3rem;
}
.flow-whatsapp-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 3px;
  margin-top: 0.3rem;
  color: #8696A0;
  font-size: 0.6rem;
}

/* Estágio 3: página de bio */
.flow-bio-card {
  width: 148px;
  background: #FFFFFF;
  border-radius: 1.25rem;
  padding: 1.25rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  box-shadow: 0 20px 40px rgba(16,24,39,0.18);
}
.flow-bio-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #0277BD;
  margin-bottom: 4px;
}
.flow-bio-row {
  display: block;
  width: 100%;
  height: 16px;
  border-radius: 999px;
  background: #E3F1FA;
  border: 1px solid rgba(2,119,189,0.25);
}
.flow-bio-row-sm {
  width: 70%;
}

/* Estágio 4: QR code personalizável, com a marca no centro */
.flow-qr-card {
  position: relative;
  width: 132px;
  height: 132px;
  background: #FFFFFF;
  border-radius: 1.25rem;
  padding: 12px;
  box-shadow: 0 20px 40px rgba(16,24,39,0.18);
}
.flow-qr-grid {
  display: grid;
  grid-template-columns: repeat(9, 1fr);
  grid-template-rows: repeat(9, 1fr);
  gap: 2px;
  width: 100%;
  height: 100%;
}
.flow-qr-cell {
  border-radius: 1px;
}
.flow-qr-cell-on {
  background: #101827;
}
.flow-qr-logo {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #FFFFFF;
  box-shadow: 0 0 0 3px #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
}
.flow-qr-logo-dot {
  position: absolute;
  width: 18px;
  height: 18px;
  border-radius: 50%;
}
.flow-qr-logo-dot-1 {
  background: #0277BD;
  left: 7px;
}
.flow-qr-logo-dot-2 {
  background: #00BDB3;
  right: 7px;
  opacity: 0.9;
}
.flow-qr-customize {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 0.7rem;
  background: #FFFFFF;
  border-radius: 999px;
  padding: 0.4rem 0.8rem;
  box-shadow: 0 10px 25px rgba(16,24,39,0.18);
  color: #101827;
  font-weight: 600;
  font-size: 0.72rem;
}
.hero-flow-stage-qrcode {
  flex-direction: column;
}

/* Estágio 5: métricas e origem do clique */
.flow-metric-panel {
  width: 168px;
  background: #FFFFFF;
  border-radius: 1.25rem;
  padding: 1.1rem 1.1rem 0.9rem;
  box-shadow: 0 20px 40px rgba(16,24,39,0.18);
}
.flow-metric-bars {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 56px;
}
.flow-metric-bar {
  flex: 1;
  height: var(--h);
  border-radius: 4px 4px 0 0;
  background: #00BDB3;
  opacity: 0.85;
}
.flow-metric-bar:nth-child(3) {
  opacity: 1;
}
.flow-metric-tag {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 0.6rem;
  color: #101827;
  font-weight: 600;
  font-size: 0.7rem;
}
.hero-anim-toggle {
  position: absolute;
  bottom: 3%;
  right: 8%;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #FFFFFF;
  border: none;
  box-shadow: 0 8px 18px rgba(16,24,39,0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
}
.hero-badge {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #FFFFFF;
  border-radius: 999px;
  padding: 0.5rem 0.9rem;
  box-shadow: 0 10px 25px rgba(16,24,39,0.12);
  font-weight: 600;
  color: #101827;
  font-size: 0.78rem;
}
.hero-badge-top {
  top: 4%;
  right: -4%;
}
.hero-badge-bottom {
  bottom: 6%;
  left: -8%;
}
.social-icon-fade-enter-active,
.social-icon-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.social-icon-fade-enter,
.social-icon-fade-leave-to {
  opacity: 0;
  transform: scale(0.4);
}
@media (prefers-reduced-motion: reduce) {
  .social-icon-fade-enter-active,
  .social-icon-fade-leave-active {
    transition: none;
  }
}

.social-proof-section {
  background: #E3F1FA;
}
.proof-headline {
  max-width: 640px;
  font-size: 1.9rem;
}
.testimonial-card {
  background: #FFFFFF;
  border-radius: 1.25rem;
  padding: 1.75rem;
  height: 100%;
  box-shadow: 0 20px 40px rgba(16,24,39,0.06);
}
.testimonial-quote {
  color: #101827;
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 1.25rem;
}
.testimonial-author {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.testimonial-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  flex: none;
}
.testimonial-name {
  font-weight: 700;
  color: #101827;
  font-size: 0.9rem;
}
.testimonial-role {
  color: #5B6472;
  font-size: 0.8rem;
}
.testimonial-social {
  display: inline-block;
  position: relative;
  color: #0277BD;
  font-size: 0.78rem;
  font-weight: 600;
  text-decoration: none;
}
.testimonial-social::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -2px;
  width: 100%;
  height: 1px;
  background: currentColor;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s cubic-bezier(0.16,1,0.3,1);
}
.testimonial-social:hover::after {
  transform: scaleX(1);
}

.section-title-dark {
  font-weight: 800;
  font-size: 2.1rem;
  color: #fff;
  text-transform: lowercase;
  line-height: 1.25;
}
.section-text-light {
  color: #B7BFCC;
  font-size: 1.05rem;
  line-height: 1.7;
  margin-top: 1rem;
  max-width: 560px;
}
.quick-benefits-section {
  background: #F7F5EF;
  border-top: 1px solid rgba(16,24,39,0.06);
}
.quick-benefit {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.7rem;
}
.quick-benefit-icon {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.quick-benefit-text {
  font-size: 0.82rem;
  font-weight: 600;
  color: #101827;
  max-width: 140px;
}

/* URL personalizável */
.mini-link-pill-v2 {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #FFFFFF;
  border: 1px solid rgba(16,24,39,0.08);
  border-radius: 999px;
  padding: 0.5rem 0.8rem;
  box-shadow: 0 8px 18px rgba(16,24,39,0.06);
}
.mini-url-text {
  font-size: 0.7rem;
  font-weight: 600;
  color: #101827;
}
.mini-edit-badge {
  position: absolute;
  bottom: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #0277BD;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0,121,241,0.4);
}

/* Métrica de acessos */
.mini-chart-bars {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  height: 40px;
}
.mini-chart-bars span {
  width: 10px;
  height: var(--h);
  border-radius: 3px 3px 0 0;
  background: #0277BD;
  opacity: 0.85;
}
.mini-chart-bars span:last-child {
  opacity: 1;
}

/* Preview para redes sociais */
.mini-og-card {
  width: 72px;
  background: #FFFFFF;
  border: 1px solid rgba(16,24,39,0.08);
  border-radius: 0.6rem;
  padding: 0.4rem;
  box-shadow: 0 8px 18px rgba(16,24,39,0.06);
}
.mini-og-image {
  width: 100%;
  height: 30px;
  border-radius: 0.35rem;
  background: #E3F1FA;
  margin-bottom: 6px;
}
.mini-og-line {
  height: 5px;
  border-radius: 3px;
  background: rgba(16,24,39,0.15);
  margin-bottom: 4px;
}
.mini-og-line-sm {
  width: 60%;
}

/* Integração Google Analytics */
.mini-trend-svg {
  width: 60px;
  height: 40px;
}

.pillars-section {
  background: #F7F5EF;
}
.section-title {
  font-weight: 800;
  font-size: 2.1rem;
  color: #0277BD;
  text-transform: lowercase;
  max-width: 720px;
}

.pillars-grid {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 1.5rem;
}
.pillars-grid-side {
  display: grid;
  grid-template-rows: 1fr 1fr;
  gap: 1.5rem;
}
.pillar-card {
  background: #FFFFFF;
  border: 1px solid rgba(16,24,39,0.08);
  border-radius: 1.25rem;
  padding: 2rem 1.75rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.pillar-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(16,24,39,0.08);
}
.pillar-card-featured {
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.pillar-illustration {
  height: 92px;
  display: flex;
  align-items: center;
  margin-bottom: 1.25rem;
}
.pillar-card-featured .pillar-illustration {
  height: 130px;
}
.origin-svg {
  width: 100%;
  max-width: 200px;
  height: auto;
}
.pillar-illustration-bio {
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
}
.mini-link-pill {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #E3F1FA;
  display: flex;
  align-items: center;
  justify-content: center;
}
.mini-connector {
  width: 2px;
  height: 14px;
  background: rgba(0,121,241,0.3);
  margin-left: 15px;
}
.mini-bio-card {
  background: #F7F5EF;
  border: 1px solid rgba(16,24,39,0.08);
  border-radius: 0.9rem;
  padding: 0.7rem 0.8rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  width: 96px;
}
.mini-bio-avatar {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #0277BD;
  margin-bottom: 2px;
}
.mini-bio-row {
  display: block;
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: #FFFFFF;
  border: 1px solid rgba(0,121,241,0.2);
}
.mini-bio-row-sm {
  width: 65%;
}
.mini-browser-wrap {
  position: relative;
  width: 100%;
  max-width: 168px;
}
.mini-browser {
  width: 100%;
  border-radius: 0.75rem;
  overflow: hidden;
  border: 1px solid rgba(16,24,39,0.1);
  box-shadow: 0 12px 24px rgba(16,24,39,0.08);
}
.mini-browser-extension-icon {
  position: absolute;
  top: -10px;
  right: -10px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #FFFFFF;
  box-shadow: 0 6px 14px rgba(16,24,39,0.18);
  padding: 3px;
}
.mini-browser-bar {
  background: #EDEBE3;
  display: flex;
  gap: 5px;
  padding: 0.5rem 0.6rem;
}
.mini-browser-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgba(16,24,39,0.2);
}
.mini-browser-address {
  background: #FFFFFF;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0.6rem 0.7rem;
}
.mini-browser-url {
  display: block;
  flex: 1;
  height: 8px;
  border-radius: 999px;
  background: #E3F1FA;
}
.pillar-title {
  font-weight: 700;
  font-size: 1.15rem;
  color: #0277BD;
  margin-bottom: 0.5rem;
}
.pillar-card-featured .pillar-title {
  font-size: 1.4rem;
}
.pillar-desc {
  color: #5B6472;
  font-size: 0.92rem;
  line-height: 1.6;
  margin-bottom: 0.75rem;
}
.pillar-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
}
.pillar-toggle {
  background: none;
  border: none;
  padding: 0;
  color: #0277BD;
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  text-decoration: none;
}
.pillar-toggle-icon-open {
  transform: rotate(180deg);
}
.pillar-detail {
  color: #5B6472;
  font-size: 0.85rem;
  line-height: 1.6;
  margin-top: 0.75rem;
  margin-bottom: 0;
  border-top: 1px solid rgba(16,24,39,0.08);
  padding-top: 0.75rem;
}
.pillars-seo-links {
  color: #5B6472;
  font-size: 0.95rem;
}
.pillars-seo-links >>> a {
  color: #0277BD;
  font-weight: 700;
  text-decoration: none;
}
.pillars-seo-links >>> a:hover {
  text-decoration: underline;
}

.bio-section {
  --bio-section-bg: #E3F1FA;
  background: var(--bio-section-bg);
}
.bio-tag {
  background: #E3F1FA;
  color: #0277BD;
}
.bio-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-width: 480px;
}
.bio-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  padding: 0.75rem 0;
  border-top: 1px solid rgba(16,24,39,0.08);
}
.bio-list li:first-child {
  border-top: none;
}
.bio-list-title {
  font-weight: 700;
  color: #101827;
  font-size: 0.95rem;
}
.bio-list-desc {
  color: #5B6472;
  font-size: 0.85rem;
  margin-top: 2px;
  line-height: 1.5;
}
.bio-mock {
  display: flex;
  justify-content: center;
}
.bio-mock-frame {
  width: 260px;
  background: #FFFFFF;
  border-radius: 1.5rem;
  padding: 2rem 1.25rem 1.5rem;
  box-shadow: 0 30px 60px rgba(16,24,39,0.12);
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: background 0.3s ease;
}
.bio-mock-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0277BD 0%, #00BDB3 100%) center/cover no-repeat;
  border: 2px solid rgba(255,255,255,0.6);
  box-shadow: 0 4px 14px rgba(16,24,39,0.18);
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.bio-mock-name {
  font-weight: 800;
  color: #101827;
  font-size: 0.95rem;
  text-align: center;
}
.bio-mock-bio {
  color: #5B6472;
  font-size: 0.75rem;
  margin: 0.2rem 0 1.25rem;
  text-align: center;
}
.bio-mock-link {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #F7F5EF;
  border: 1px solid rgba(16,24,39,0.08);
  border-radius: 999px;
  padding: 0.65rem 1rem;
  margin-bottom: 0.6rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #101827;
}
.bio-mock-link:last-child {
  margin-bottom: 0;
}
/* Hand-simulated sales-page preview, same approach as .bio-mock above:
   InfoproductView is a full standalone page (header, item list, description,
   form, footer) built with responsive/vw-based type sizing meant for a full
   browser viewport - squeezed into a ~300px phone frame it breaks badly
   (the title alone rendered at ~4rem, spilling out of the frame). Rather
   than fight that component's CSS, this is a static mock with its own
   fixed, frame-appropriate sizing - it can never break because it never
   reads real infoproduct data. */
.infoproduct-mock {
  display: flex;
  justify-content: center;
}
.infoproduct-mock-bezel {
  width: 260px;
  background: #101827;
  border-radius: 2.2rem;
  padding: 12px 10px 16px;
  box-shadow: 0 30px 60px rgba(16,24,39,0.18);
}
.infoproduct-mock-notch {
  width: 50px;
  height: 16px;
  background: #101827;
  border-radius: 0 0 12px 12px;
  margin: 0 auto 6px;
}
.infoproduct-mock-screen {
  background: #FFFFFF;
  border-radius: 1.6rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.infoproduct-mock-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.8rem 1rem;
  background: linear-gradient(135deg, #0277BD 0%, #00BDB3 100%);
  color: #FFFFFF;
  font-size: 0.68rem;
  font-weight: 700;
}
.infoproduct-mock-logo {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(255,255,255,0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.infoproduct-mock-hero {
  padding: 1.5rem 1.1rem 1.15rem;
  text-align: center;
  background: linear-gradient(180deg, #E9F4FF 0%, #FFFFFF 70%);
}
.infoproduct-mock-badge {
  display: inline-block;
  background: #FFFFFF;
  border: 1px solid rgba(2,119,189,0.3);
  color: #0277BD;
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  margin-bottom: 0.75rem;
}
.infoproduct-mock-title {
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.3;
  color: #101827;
  margin: 0 0 0.5rem;
}
.infoproduct-mock-desc {
  font-size: 0.72rem;
  color: #5B6472;
  line-height: 1.5;
  margin: 0 0 1rem;
}
.infoproduct-mock-cta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #0277BD;
  color: #FFFFFF;
  font-weight: 700;
  font-size: 0.75rem;
  padding: 0.55rem 1.1rem;
  border-radius: 999px;
  box-shadow: 0 10px 20px rgba(2,119,189,0.28);
}
.infoproduct-mock-topics {
  list-style: none;
  margin: 0;
  padding: 0.9rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  border-top: 1px solid rgba(16,24,39,0.06);
}
.infoproduct-mock-topics li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.68rem;
  font-weight: 600;
  color: #252F3F;
}
.infoproduct-mock-form {
  display: flex;
  gap: 6px;
  padding: 0.9rem 1.1rem 1.1rem;
  background: #F7F5EF;
}
.infoproduct-mock-form-input {
  flex: 1;
  background: #FFFFFF;
  border: 1px solid rgba(16,24,39,0.12);
  border-radius: 999px;
  padding: 0.5rem 0.8rem;
  font-size: 0.62rem;
  color: #9AA5B1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.infoproduct-mock-form-btn {
  background: #101827;
  color: #FFFFFF;
  font-size: 0.62rem;
  font-weight: 700;
  padding: 0.5rem 0.75rem;
  border-radius: 999px;
  white-space: nowrap;
}
.infoproduct-mock-home-indicator {
  width: 44px;
  height: 4px;
  background: rgba(255,255,255,0.5);
  border-radius: 999px;
  margin: 10px auto 0;
}
.infoproduct-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-width: 480px;
}
.infoproduct-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  padding: 0.75rem 0;
  border-top: 1px solid rgba(16,24,39,0.08);
}
.infoproduct-list li:first-child {
  border-top: none;
}
.infoproduct-list-title {
  font-weight: 700;
  color: #101827;
  font-size: 0.95rem;
}
.infoproduct-list-desc {
  color: #5B6472;
  font-size: 0.85rem;
  margin-top: 2px;
  line-height: 1.5;
}
.bio-examples-label {
  text-align: center;
  color: #5B6472;
  font-weight: 700;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0 0 1.5rem;
}
.bio-examples-gallery-wrap {
  position: relative;
  overflow: hidden;
}
.bio-examples-gallery {
  display: flex;
  gap: 1.75rem;
  width: max-content;
  padding: 1.5rem 1rem 2.5rem;
  animation: bio-examples-scroll 32s linear infinite;
}
.bio-examples-gallery-wrap:hover .bio-examples-gallery {
  animation-play-state: paused;
}
@keyframes bio-examples-scroll {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
}
.bio-examples-fade {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 64px;
  pointer-events: none;
  z-index: 1;
}
.bio-examples-fade-left {
  left: 0;
  background: linear-gradient(90deg, var(--bio-section-bg) 0%, transparent 100%);
}
.bio-examples-fade-right {
  right: 0;
  background: linear-gradient(270deg, var(--bio-section-bg) 0%, transparent 100%);
}
.bio-example-phone {
  flex: 0 0 auto;
  width: 168px;
  transform: rotate(var(--tilt, 0deg));
  transition: transform 0.3s ease;
}
.bio-example-phone:hover {
  transform: rotate(0deg) translateY(-6px);
}
.bio-example-phone:nth-child(even) {
  margin-top: 1.5rem;
}
@media (prefers-reduced-motion: reduce) {
  .bio-examples-gallery {
    animation: none;
  }
  .bio-example-phone {
    transition: none;
  }
}
.bio-example-niche {
  display: block;
  text-align: center;
  font-size: 0.66rem;
  font-weight: 700;
  color: #0277BD;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 0.6rem;
}
.bio-example-bezel {
  background: #101827;
  border-radius: 1.9rem;
  padding: 10px 8px 14px;
}
.bio-example-notch {
  width: 44px;
  height: 14px;
  background: #101827;
  border-radius: 0 0 10px 10px;
  margin: 0 auto 4px;
}
.bio-example-screen {
  border-radius: 1.3rem;
  overflow: hidden;
  padding: 1.4rem 0.65rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 250px;
}
.bio-example-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0277BD 0%, #00BDB3 100%) center/cover no-repeat;
  border: 2px solid rgba(255,255,255,0.6);
  margin-bottom: 0.5rem;
  flex-shrink: 0;
}
.bio-example-avatar-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}
.bio-example-name {
  font-weight: 800;
  font-size: 0.76rem;
  text-align: center;
}
.bio-example-tagline {
  font-size: 0.6rem;
  text-align: center;
  margin: 0.15rem 0 0.9rem;
  opacity: 0.9;
}
.bio-example-link {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.92);
  border-radius: 999px;
  padding: 0.45rem 0.6rem;
  margin-bottom: 0.4rem;
  font-size: 0.6rem;
  font-weight: 700;
  color: #101827;
}
.bio-example-link:last-child {
  margin-bottom: 0;
}
.bio-example-home-indicator {
  width: 44px;
  height: 4px;
  background: rgba(255,255,255,0.5);
  border-radius: 999px;
  margin: 10px auto 0;
}

.og-section {
  background: #FFFFFF;
  /* Reserves the rendered height while its content is lazily mounted
     (see `revealed.og`), so revealing it doesn't shift layout below it.
     855px measured desktop (1440px viewport); 1500px is a conservative
     mobile estimate (cols="12" md="6" stacks to one column + narrower-
     width text rewraps taller) — see lazy-render-homepage-below-fold-
     sections/tasks.md 1.1 for methodology and the real-device caveat. */
  min-height: 855px;
}
@media (max-width: 960px) {
  .og-section {
    min-height: 1500px;
  }
}
.og-mock {
  display: flex;
  justify-content: center;
}
.og-title-text {
  font-weight: 700;
  font-size: 0.7rem;
  color: #101827;
  line-height: 1.3;
  margin-bottom: 3px;
}
.og-desc-text {
  font-size: 0.6rem;
  color: #5B6472;
  line-height: 1.4;
  margin-bottom: 4px;
}

/* Simulação de tela de WhatsApp: link "enviado" e a prévia de Open Graph
   carregando (shimmer) até resolver no conteúdo customizado, em loop. */
.wa-frame {
  width: 100%;
  max-width: 300px;
  background: #FFFFFF;
  border-radius: 1.5rem;
  overflow: hidden;
  box-shadow: 0 30px 60px rgba(16,24,39,0.12);
  border: 1px solid rgba(16,24,39,0.06);
}
.wa-frame-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.8rem 1rem;
  border-bottom: 1px solid rgba(16,24,39,0.06);
}
.wa-frame-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #0277BD;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}
.wa-frame-header-text {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}
.wa-frame-name {
  font-weight: 700;
  font-size: 0.85rem;
  color: #101827;
}
.wa-frame-status {
  font-size: 0.68rem;
  color: #5B6472;
}
.wa-frame-icon {
  margin-left: auto;
}
.wa-frame-body {
  background: #F7F5EF;
  padding: 1.5rem 1rem;
  min-height: 200px;
  display: flex;
  align-items: center;
}
.wa-bubble {
  animation: wa-bubble-pop 9s ease infinite;
}
@keyframes wa-bubble-pop {
  0% { opacity: 0; transform: translateY(14px) scale(0.94); }
  4% { opacity: 1; transform: translateY(0) scale(1.02); }
  8%, 93% { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0; transform: translateY(-8px) scale(0.97); }
}
.wa-og-text-stack {
  position: relative;
  min-height: 34px;
}
.wa-loading-layer,
.wa-final-layer {
  position: absolute;
  inset: 0;
}
.wa-loading-layer {
  animation: wa-loading-fade 9s ease infinite;
}
.wa-final-layer {
  animation: wa-final-fade 9s ease infinite;
}
.wa-text-layer {
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.wa-final-image {
  background: linear-gradient(135deg, #0277BD, #00BDB3);
  display: flex;
  align-items: center;
  justify-content: center;
}
@keyframes wa-loading-fade {
  0% { opacity: 0; }
  5% { opacity: 1; }
  58% { opacity: 1; }
  66% { opacity: 0; }
  100% { opacity: 0; }
}
@keyframes wa-final-fade {
  0%, 60% { opacity: 0; }
  68% { opacity: 1; }
  90% { opacity: 1; }
  100% { opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .wa-bubble {
    animation: none;
    opacity: 1;
  }
  .wa-loading-layer {
    animation: none;
    opacity: 0;
  }
  .wa-final-layer {
    animation: none;
    opacity: 1;
  }
}

/* Carrossel de redes sociais: mesma prévia do link, mostrada como cada
   rede realmente a exibe (bolha de chat no WhatsApp/Instagram, card de
   post no Facebook/LinkedIn). */
.sn-carousel-wrap {
  width: 100%;
  max-width: 320px;
}
.sn-slide-label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #5B6472;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 0.6rem;
}
.sn-carousel ::v-deep .VueCarousel-slide {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.sn-carousel ::v-deep .VueCarousel-pagination {
  margin-top: 0.75rem;
}
.sn-carousel ::v-deep .VueCarousel-navigation-button {
  color: #0277BD;
  font-size: 1.6rem;
  font-weight: 700;
  top: 46%;
}
.sn-carousel ::v-deep .VueCarousel-navigation-button.VueCarousel-navigation--disabled {
  opacity: 0.25;
}

/* Instagram: mesma estrutura de bolha do WhatsApp, com avatar em anel
   degradê e link já resolvido (sem etapa de carregamento). */
.ig-avatar {
  background: linear-gradient(135deg, #F58529, #DD2A7B, #8134AF);
}
.ig-bubble {
  width: 190px;
  background: #FFFFFF;
  border: 1px solid rgba(16,24,39,0.1);
  border-radius: 1rem 1rem 1rem 0.2rem;
  padding: 0.6rem 0.6rem 0.5rem;
  box-shadow: 0 20px 40px rgba(16,24,39,0.1);
}

/* Facebook / LinkedIn: card de post de feed, com cabeçalho, legenda e o
   OG card nas proporções maiores usadas nesses feeds. */
.feed-frame {
  width: 100%;
  max-width: 300px;
  background: #FFFFFF;
  border: 1px solid rgba(16,24,39,0.08);
  border-radius: 1rem;
  padding: 1rem;
  box-shadow: 0 30px 60px rgba(16,24,39,0.12);
}
.feed-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0.7rem;
}
.feed-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}
.feed-header-text {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}
.feed-name {
  font-weight: 700;
  font-size: 0.82rem;
  color: #101827;
}
.feed-meta {
  font-size: 0.65rem;
  color: #5B6472;
}
.feed-header .v-icon {
  margin-left: auto;
}
.feed-caption {
  font-size: 0.78rem;
  color: #101827;
  line-height: 1.5;
  margin-bottom: 0.7rem;
}
.feed-og-card {
  width: 100%;
}
.feed-og-image {
  height: 110px;
}
.feed-og-card .og-title-text {
  font-size: 0.85rem;
}
.feed-og-card .og-desc-text {
  font-size: 0.72rem;
}
.feed-og-card .wa-og-text-stack {
  min-height: 44px;
}
.feed-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 0.7rem;
  padding-top: 0.6rem;
  border-top: 1px solid rgba(16,24,39,0.08);
}
.feed-actions span {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.68rem;
  font-weight: 600;
  color: #5B6472;
}

.promo-section {
  background: #F0FBFA;
}
.section-text {
  color: #5B6472;
  font-size: 1rem;
  line-height: 1.7;
  max-width: 480px;
}
.promo-plan-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #FFFFFF;
  color: #00A89F;
  font-weight: 700;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-radius: 999px;
  padding: 0.35rem 0.8rem;
  margin-bottom: 1rem;
  box-shadow: 0 8px 18px rgba(16,24,39,0.06);
}
.section-coming-soon-tag {
  display: inline-flex;
  align-items: center;
  background: #0277BD;
  color: #FFFFFF;
  font-weight: 800;
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-radius: 999px;
  padding: 0.35rem 0.7rem;
  margin-left: 0.5rem;
  margin-bottom: 1rem;
  vertical-align: middle;
}
.promo-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-width: 480px;
}
.promo-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  color: #101827;
  font-size: 0.95rem;
  font-weight: 600;
  padding: 0.6rem 0;
  border-top: 1px solid rgba(16,24,39,0.08);
}
.promo-list li:first-child {
  border-top: none;
}
.promo-mock {
  display: flex;
  justify-content: center;
}
.promo-mock-frame {
  width: 260px;
  background: #F7F5EF;
  border-radius: 1.5rem;
  padding: 1.25rem;
  box-shadow: 0 30px 60px rgba(16,24,39,0.12);
}
.promo-mock-custom-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #0277BD;
  font-weight: 700;
  font-size: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.6rem;
}
.promo-mock-ad-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #00A89F;
  font-weight: 700;
  font-size: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0.9rem 0 0.5rem;
}
.promo-mock-brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.9rem;
}
.promo-mock-brand-logo {
  width: 36px;
  height: 36px;
  border-radius: 0.9rem;
  background: linear-gradient(135deg, #0277BD 0%, #00BDB3 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 18px rgba(2,119,189,0.28);
}
.promo-mock-brand-name {
  font-size: 0.8rem;
  font-weight: 800;
  color: #101827;
}
.promo-mock-redirect-card {
  background: #FFFFFF;
  border-radius: 0.9rem;
  padding: 0.8rem;
  text-align: center;
  box-shadow: 0 8px 18px rgba(16,24,39,0.06);
}
.promo-mock-spinner {
  width: 18px;
  height: 18px;
  margin: 0 auto 0.5rem;
  border-radius: 50%;
  border: 2px solid #E3F1FA;
  border-top-color: #0277BD;
  animation: promo-mock-spin 0.9s linear infinite;
}
@keyframes promo-mock-spin {
  100% { transform: rotate(360deg); }
}
@media (prefers-reduced-motion: reduce) {
  .promo-mock-spinner {
    animation: none;
  }
}
.promo-mock-redirect-label {
  font-size: 0.68rem;
  color: #5B6472;
  margin: 0 0 0.15rem;
}
.promo-mock-redirect-url {
  font-size: 0.75rem;
  font-weight: 700;
  color: #101827;
  margin: 0;
}
.promo-mock-banner {
  position: relative;
  height: 92px;
  border-radius: 0.9rem;
  background: linear-gradient(135deg, #E3F1FA 0%, #DFF7F4 100%);
  overflow: hidden;
  margin-bottom: 0.9rem;
  display: flex;
  align-items: flex-end;
}
.promo-mock-banner-shimmer {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.7) 50%, rgba(255,255,255,0) 100%);
  background-size: 200% 100%;
  animation: og-shimmer-sweep 1.6s linear infinite;
}
@media (prefers-reduced-motion: reduce) {
  .promo-mock-banner-shimmer {
    display: none;
  }
}
.promo-mock-product {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  width: calc(100% - 16px);
  margin: 8px;
  background: #FFFFFF;
  border-radius: 0.7rem;
  padding: 6px 8px;
  box-shadow: 0 10px 20px rgba(16,24,39,0.1);
}
.promo-mock-product-thumb {
  width: 30px;
  height: 30px;
  border-radius: 0.4rem;
  background: linear-gradient(135deg, #0277BD 0%, #00BDB3 100%);
  flex-shrink: 0;
}
.promo-mock-product-info {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}
.promo-mock-product-title {
  font-size: 0.62rem;
  color: #5B6472;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.promo-mock-product-price {
  font-size: 0.75rem;
  font-weight: 800;
  color: #101827;
}
.promo-mock-product-cta {
  flex-shrink: 0;
  background: #0277BD;
  color: #fff;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 0.3rem 0.55rem;
  border-radius: 999px;
}
.promo-mock-timer {
  font-size: 0.72rem;
  color: #5B6472;
  text-align: center;
  margin-top: 0.4rem;
}

.calculator-section {
  background: #101827;
  color: #fff;
  /* Reserves the rendered height while its content is lazily mounted
     (see `revealed.calculator`), so revealing it doesn't shift layout
     below it. 1447px measured desktop (1440px viewport, two stacked
     v-rows: the slider/result row plus a chart-preview row); 2200px is
     a conservative mobile estimate (both rows use cols="12" md="6" and
     stack on mobile, compounding) — see lazy-render-homepage-below-
     fold-sections/tasks.md 1.1 for methodology and the real-device
     caveat. */
  min-height: 1447px;
}
@media (max-width: 960px) {
  .calculator-section {
    min-height: 2200px;
  }
}
.calc-label {
  display: block;
  color: #B7BFCC;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.calc-slider {
  width: 100%;
  accent-color: #0277BD;
}
.calc-value {
  color: #fff;
  font-weight: 700;
  font-size: 1.1rem;
  margin-top: 0.5rem;
}
.calc-result-card {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 1.5rem;
  padding: 2rem;
  text-align: center;
}
.calc-result-label {
  color: #B7BFCC;
  font-size: 0.85rem;
  text-transform: lowercase;
}
.calc-result-number {
  color: #00BDB3;
  font-weight: 900;
  font-size: 3rem;
  line-height: 1.1;
  margin: 0.5rem 0;
}
.calc-result-caption {
  color: #E4E8EF;
  font-size: 0.95rem;
}
.calc-data-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem;
  text-align: left;
}
.calc-data-item {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 0.75rem;
  padding: 0.55rem 0.7rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #E4E8EF;
}
.calc-data-item-wide {
  grid-column: 1 / -1;
}
.calc-charts-heading {
  color: #fff;
  font-weight: 800;
  font-size: 1.5rem;
  margin-bottom: 0.4rem;
}
.calc-charts-subheading {
  color: #B7BFCC;
  font-size: 0.95rem;
  margin-bottom: 1.5rem;
}
.calc-chart-card {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 1.25rem;
  padding: 1.25rem 1rem 0.5rem;
  height: 100%;
}
.calc-chart-card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #E4E8EF;
  font-weight: 700;
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
  padding: 0 0.5rem;
}

/* Gráfico de linha (comparação de período) — SVG desenhado à mão */
.chart-line-legend {
  display: flex;
  gap: 1.2rem;
  padding: 0 0.5rem;
  margin-bottom: 0.5rem;
}
.chart-line-legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #B7BFCC;
  font-size: 0.75rem;
  font-weight: 600;
}
.chart-line-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}
.chart-line-dot-current {
  background: #00BDB3;
}
.chart-line-dot-previous {
  background: #B7C2D9;
}
.chart-line-canvas {
  position: relative;
  width: 100%;
  height: 130px;
}
.chart-line-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
}
.chart-line-point {
  position: absolute;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
}
.chart-line-point-previous {
  width: 6px;
  height: 6px;
  background: #B7C2D9;
}
.chart-line-point-current {
  width: 7px;
  height: 7px;
  background: #00BDB3;
}
.chart-line-labels {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0.5rem 0;
  color: #B7BFCC;
  font-size: 0.72rem;
  text-transform: capitalize;
}

/* Gráficos de barra horizontal — QR Code / Rede usada pra compartilhar */
.chart-hbar-list {
  padding: 0.5rem;
}
.chart-hbar-row {
  display: grid;
  grid-template-columns: 76px 1fr 44px;
  align-items: center;
  gap: 10px;
  margin-bottom: 0.9rem;
}
.chart-hbar-row:last-child {
  margin-bottom: 0;
}
.chart-hbar-label {
  color: #E4E8EF;
  font-size: 0.78rem;
  font-weight: 600;
}
.chart-hbar-track {
  background: rgba(255,255,255,0.08);
  border-radius: 999px;
  height: 10px;
  overflow: hidden;
}
.chart-hbar-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #00BDB3 0%, #0277BD 100%);
  transition: width 0.6s cubic-bezier(0.16,1,0.3,1);
}
.chart-hbar-value {
  color: #B7BFCC;
  font-size: 0.75rem;
  font-weight: 700;
  text-align: right;
}

/* Gráfico de colunas — Acessos por período */
.chart-columns {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  height: 140px;
  padding: 0.5rem 0.75rem 0;
}
.chart-column-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  justify-content: flex-end;
  gap: 0.5rem;
}
.chart-column-bar {
  width: 100%;
  max-width: 28px;
  height: var(--h);
  border-radius: 6px 6px 0 0;
  background: linear-gradient(180deg, #0277BD 0%, #4FA8E0 100%);
}
.chart-column-label {
  color: #B7BFCC;
  font-size: 0.7rem;
  text-transform: capitalize;
}

/* Gráfico de rosca — Origem de tráfego UTM */
.chart-donut-row {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 0.5rem 0.5rem 1rem;
}
.chart-donut {
  position: relative;
  width: 110px;
  height: 110px;
  border-radius: 50%;
  flex-shrink: 0;
}
.chart-donut-hole {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #131C2B;
}
.chart-donut-legend {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
}
.chart-donut-legend-item {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #E4E8EF;
  font-size: 0.78rem;
}
.chart-donut-legend-item span {
  flex: 1;
}
.chart-donut-legend-item b {
  color: #B7BFCC;
}
.chart-donut-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}

.api-section {
  background: #FFFFFF;
}
.api-mock {
  display: flex;
  justify-content: center;
}
.api-mock-frame {
  width: 100%;
  max-width: 400px;
  background: #101827;
  border-radius: 1.25rem;
  overflow: hidden;
  box-shadow: 0 30px 60px rgba(16,24,39,0.18);
}
.api-mock-topbar {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0.7rem 0.9rem;
  background: rgba(255,255,255,0.04);
  border-bottom: 1px solid rgba(255,255,255,0.08);
}
.api-mock-topbar .mini-browser-dot {
  background: rgba(255,255,255,0.2);
}
.api-mock-topbar-title {
  margin-left: auto;
  font-size: 0.68rem;
  font-weight: 600;
  color: #7E8EA6;
}
.api-mock-body {
  padding: 1rem 1.1rem 1.25rem;
  font-family: 'Menlo', 'Consolas', monospace;
  font-size: 0.72rem;
  line-height: 1.7;
  color: #B7BFCC;
  overflow-x: auto;
}
.api-mock-line {
  white-space: pre;
}
.api-mock-indent {
  padding-left: 1.2em;
}
.api-mock-divider {
  height: 1px;
  background: rgba(255,255,255,0.08);
  margin: 0.7rem 0;
}
.tok-key {
  color: #4FC3F7;
}
.tok-string {
  color: #00BDB3;
}
.tok-comment {
  color: #5B6472;
}

.plans-section {
  background: #FFFFFF;
}
.pricing-testimonial-wrap {
  max-width: 560px;
  margin: 0 auto 2.5rem;
}
.pricing-testimonial-card {
  border: 1px solid rgba(16,24,39,0.06);
}

/*
 * `<SubscriptionAndPlans>` é um componente compartilhado, usado em várias
 * telas logadas do produto (painel, config, etc.) — não editamos o arquivo
 * em si para não afetar essas outras telas. Aqui só reskinamos visualmente,
 * via ::v-deep, pra bater com a paleta/tipografia/raio da home nova: título
 * indigo → ink, aba ativa ciano → branco/azul primário, cantos retos →
 * arredondados. Os overrides de `.primary--text`/`.v-card.primary` abaixo
 * ficam redundantes agora que o tema do Vuetify usa a cor certa por
 * padrão, mas mantemos como rede de segurança.
 */
.plans-skin ::v-deep * {
  font-family: 'Montserrat', sans-serif;
}
.plans-skin ::v-deep .header-title.primary--text,
.plans-skin ::v-deep .header-title {
  color: #101827 !important;
}
.plans-skin ::v-deep .v-tabs .tab-item-subscrible-year {
  background-color: #FFFFFF !important;
  color: #0277BD !important;
}
.plans-skin ::v-deep .v-tabs .color-green-blue {
  color: rgba(255,255,255,0.85) !important;
}
.plans-skin ::v-deep .v-card-title .color-green-blue {
  color: #0277BD !important;
}
.plans-skin ::v-deep .primary--text {
  color: #0277BD !important;
}
.plans-skin ::v-deep .v-card.primary {
  background-color: #0277BD !important;
  border-color: #0277BD !important;
}
.plans-skin ::v-deep .v-card {
  border-radius: 1.25rem !important;
}
.plans-skin ::v-deep .v-sheet--outlined {
  border-color: rgba(16,24,39,0.08) !important;
}
.plans-skin ::v-deep .VueCarousel-slide {
  display: flex;
  align-items: stretch;
}
.plans-skin ::v-deep .VueCarousel-slide > .v-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/*
 * Escala de cor dos cards de plano, do mais barato ao mais caro (plansByPrice
 * já ordena por preço; são 4 planos: gratuito, básico, profissional, empresarial):
 * branco (1º, padrão, sem override) → azul bem claro (2º, básico) → azul primário
 * escuro (3º e 4º, profissional e empresarial, mesma cor). Nos dois cards escuros
 * o texto muda pra branco (senão fica ilegível) e o botão de assinar é invertido
 * (fundo branco/texto azul) pra não sumir contra o card, já que por padrão os
 * dois usam a mesma cor primária. O `.v-list` do Vuetify pinta fundo branco por
 * conta própria — precisa ficar transparente em todo card colorido, senão a
 * lista de recursos aparece como um bloco branco por cima da cor do card.
 */
.plans-skin ::v-deep .VueCarousel-slide:nth-child(2) .v-sheet--outlined {
  background-color: #E3F1FA !important;
}
.plans-skin ::v-deep .VueCarousel-slide:nth-child(2) .v-sheet--outlined .v-list {
  background-color: transparent !important;
}
.plans-skin ::v-deep .VueCarousel-slide:nth-child(3) .v-sheet--outlined,
.plans-skin ::v-deep .VueCarousel-slide:last-child .v-sheet--outlined {
  background-color: #0277BD !important;
  border-color: #0277BD !important;
}
.plans-skin ::v-deep .VueCarousel-slide:nth-child(3) .v-sheet--outlined,
.plans-skin ::v-deep .VueCarousel-slide:nth-child(3) .v-sheet--outlined *,
.plans-skin ::v-deep .VueCarousel-slide:last-child .v-sheet--outlined,
.plans-skin ::v-deep .VueCarousel-slide:last-child .v-sheet--outlined * {
  color: #FFFFFF !important;
}
.plans-skin ::v-deep .VueCarousel-slide:nth-child(3) .v-sheet--outlined .v-card.primary,
.plans-skin ::v-deep .VueCarousel-slide:nth-child(3) .v-sheet--outlined .v-card.primary *,
.plans-skin ::v-deep .VueCarousel-slide:last-child .v-sheet--outlined .v-card.primary,
.plans-skin ::v-deep .VueCarousel-slide:last-child .v-sheet--outlined .v-card.primary * {
  background-color: #FFFFFF !important;
  color: #0277BD !important;
}
.plans-skin ::v-deep .VueCarousel-slide:nth-child(3) .v-sheet--outlined .v-list,
.plans-skin ::v-deep .VueCarousel-slide:last-child .v-sheet--outlined .v-list {
  background-color: transparent !important;
}

@media (max-width: 960px) {
  .hero-title { font-size: 2.3rem; }
  .section-title, .section-title-dark { font-size: 1.7rem; }
  .pillars-grid {
    grid-template-columns: 1fr;
  }
  .pillars-grid-side {
    grid-template-rows: none;
  }
}
</style>
